import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Avatar,
  Button,
  ImageList,
  ImageListItem,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  LinearProgress
} from '@mui/material';
import { useSnackbar } from 'notistack';
import LockIcon from '@mui/icons-material/Lock';
import {
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  EventNote as EventNoteIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Handshake as HandshakeIcon,
  CalendarMonth as CalendarMonthIcon,
  Home as HomeIcon,
  Euro as EuroIcon,
  SecurityOutlined as SecurityIcon,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import disputeAdminService from '../services/disputeAdminService';
import { reservationService } from '../services/reservationService';
import restrictionAdminService from '../services/restrictionAdminService';
import currencyConversionService from '../services/currencyConversionService';
import socketService from '../services/socketService';
import { useAuth } from '../context/AuthContext';
import CashSettlementCard from '../components/disputes/CashSettlementCard';
import AdminDecisionPanel from '../components/disputes/AdminDecisionPanel';
import PrivateChannelsPanel from '../components/disputes/PrivateChannelsPanel';
import { MonetaryPenaltyForm } from '../components/penalties';
import DisputeAuthenticationModal from '../components/disputes/DisputeAuthenticationModal';
// 🆕 Système isolé pour authentification 3DS des décisions admin
import AdminPaymentAuthBanner from '../components/disputes/AdminPaymentAuthBanner';
import AdminPaymentAuthAdminBanner from '../components/disputes/AdminPaymentAuthAdminBanner';
import AdminPaymentAuth3DSModal from '../components/disputes/AdminPaymentAuth3DSModal';
import adminPaymentAuthService from '../services/adminPaymentAuthService';
// 🆕 Analyse financière complète du dispute
import DisputeFinancialAnalysis from '../components/disputes/DisputeFinancialAnalysis';
// 🆕 Récapitulatif de l'exécution de la pénalité
import PenaltyExecutionSummary from '../components/disputes/PenaltyExecutionSummary';
// 🆕 Récapitulatif du payout de compensation de dommages
import DamageCompensationPayoutSummary from '../components/disputes/DamageCompensationPayoutSummary';
import payoutService from '../services/payoutService';
// 🆕 Récapitulatif du remboursement pour le locataire
import RenterRefundPaymentSummary from '../components/disputes/RenterRefundPaymentSummary';
import { paymentService } from '../services/paymentService';
import { getCurrencySymbol } from '../utils/currency';
import { alpha } from '@mui/material/styles';
import LoadingOverlay from '../components/common/LoadingOverlay';

// Base URL for uploads (without /api/v1)
const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

// Helper to get full URL for uploaded files
const getFileUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${UPLOADS_BASE_URL}${path}`;
};

// Workflow steps mapping
const getWorkflowSteps = (t) => [
  { key: 'pending', label: t('workflow.submitted', 'Soumis'), icon: <AssignmentIcon /> },
  { key: 'in_progress', label: t('workflow.investigation', 'En investigation'), icon: <ScheduleIcon /> },
  { key: 'pending_settlement', label: t('workflow.pending_settlement', 'En attente de règlement'), icon: <HandshakeIcon /> },
  { key: 'resolved', label: t('workflow.resolved', 'Résolu'), icon: <CheckCircleIcon /> },
  { key: 'closed', label: t('workflow.closed', 'Clôturé'), icon: <CheckCircleIcon /> }
];

const getActiveStep = (status) => {
  const statusOrder = ['pending', 'in_progress', 'pending_settlement', 'resolved', 'closed'];
  return statusOrder.indexOf(status);
};

const getStatusColor = (status) => {
  const statusColors = {
    pending: 'warning',
    in_progress: 'info',
    pending_settlement: 'secondary',
    resolved: 'success',
    closed: 'default',
    escalated: 'error'
  };
  return statusColors[status] || 'default';
};

const getStatusLabel = (status, t) => {
  const labels = {
    pending: t('status.pending', 'En attente'),
    in_progress: t('status.in_progress', 'En cours'),
    pending_settlement: t('status.pending_settlement', 'En attente de règlement'),
    resolved: t('status.resolved', 'Résolu'),
    closed: t('status.closed', 'Fermé'),
    escalated: t('status.escalated', 'Escaladé')
  };
  return labels[status] || status;
};

/**
 * Retourne l'URL de la photo de profil avec cache-buster stable
 * @param {Object} user - User object avec profilePicture
 * @returns {string} URL complète ou chaîne vide
 */
const getProfilePictureUrl = (user) => {
  if (!user?.profilePicture) return '';
  
  // ✅ FIX P2-4: Simplification cache-busting - hash court au lieu du filename complet
  // Évite Date.now() (rechargement à chaque render) tout en gardant un identifiant stable
  const url = String(user.profilePicture);
  
  // Générer un hash court et stable du path complet (invariant entre renders)
  const simpleHash = url.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0) | 0;
  }, 0);
  
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${Math.abs(simpleHash).toString(36)}`;
};

/**
 * Retourne les informations de statut pour une pénalité (couleur, icône, label traduit)
 * @param {string} status - Statut de la pénalité (pending, pending_collection, scheduled, applied, failed, waived, disputed)
 * @param {Function} t - Fonction de traduction (i18n)
 * @returns {Object} Objet contenant { color, icon, label }
 */
const getPenaltyStatusInfo = (status, t) => {
  const statusMap = {
    pending: { color: 'warning', icon: <ScheduleIcon />, label: t('penalties.execution.statuses.pending', 'En attente') },
    pending_collection: { color: 'warning', icon: <ScheduleIcon />, label: t('penalties.execution.statuses.pending_collection', 'Recouvrement en cours') },
    scheduled: { color: 'info', icon: <HistoryIcon />, label: t('penalties.execution.statuses.scheduled', 'Programmée') },
    applied: { color: 'success', icon: <CheckCircleIcon />, label: t('penalties.execution.statuses.applied', 'Appliquée') },
    failed: { color: 'error', icon: <ErrorIcon />, label: t('penalties.execution.statuses.failed', 'Échec') },
    waived: { color: 'default', icon: <HandshakeIcon />, label: t('penalties.execution.statuses.waived', 'Annulée') },
    disputed: { color: 'secondary', icon: <GavelIcon />, label: t('penalties.execution.statuses.disputed', 'Contestée') }
  };
  return statusMap[status] || { color: 'default', icon: <InfoIcon />, label: status };
};

const DisputeDetailPage = () => {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('disputes');
  const { enqueueSnackbar } = useSnackbar();
  const dateLocale = i18n?.language && i18n.language.startsWith('fr') ? fr : enUS;
  
  const [dispute, setDispute] = useState(null);
  const [fullReservation, setFullReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for new message
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  const post3dsPollingRef = useRef(false);

  const fetchDisputeInFlightRef = useRef(false);
  const fetchDisputeQueuedRef = useRef(false);
  const fetchDisputeQueuedWithRelatedRef = useRef(false);
  const fetchDisputeQueuedTimerRef = useRef(null);
  const lastFetchDisputeAtRef = useRef(0);
  
  // ✅ FIX P1-3: Circuit-breaker pour éviter boucles infinies en cas d'erreurs persistantes
  const fetchDisputeErrorCountRef = useRef(0);
  const fetchDisputeCircuitOpenRef = useRef(false);

  const adminAuthInFlightRef = useRef(false);

  // Derived: primary payment method for the reservation (normalized)
  const reservationPaymentMethod = (
    (dispute?.reservation?.payments?.[0]?.payment_method) ||
    dispute?.reservation?.payment_method ||
    ''
  ).toLowerCase();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;

      if (fetchDisputeQueuedTimerRef.current) {
        clearTimeout(fetchDisputeQueuedTimerRef.current);
        fetchDisputeQueuedTimerRef.current = null;
      }
    };
  }, []);

  // State for actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'resolve', 'close', 'escalate'
  const [actionNote, setActionNote] = useState('');
  const [compensationAmount, setCompensationAmount] = useState('');

  // State for amicable resolution
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [proposalAmount, setProposalAmount] = useState('');
  const [proposalMessage, setProposalMessage] = useState('');
  const [compensationType, setCompensationType] = useState('owner_compensation'); // 🔄 Nouveau: type de compensation
  const [proposalError, setProposalError] = useState('');

  // State for reject/counter-proposal
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [counterProposalDialogOpen, setCounterProposalDialogOpen] = useState(false);
  const [counterAmount, setCounterAmount] = useState('');
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const [counterMessage, setCounterMessage] = useState('');
  const [counterCompensationType, setCounterCompensationType] = useState('owner_compensation'); // 🔄 Type de compensation pour contre-proposition
  const [counterError, setCounterError] = useState('');

  const isRenter = !!user?.id && user.id === dispute?.reservation?.renter_id;
  const defaultCompensationType = isRenter ? 'renter_refund' : 'owner_compensation';
  const sanitizeCompensationType = (type) => (isRenter && type === 'owner_compensation' ? 'renter_refund' : type);

  useEffect(() => {
    if (!dispute || !user?.id) return;
    if (!isRenter) return;

    if (compensationType === 'owner_compensation') {
      setCompensationType('renter_refund');
    }
    if (counterCompensationType === 'owner_compensation') {
      setCounterCompensationType('renter_refund');
    }
  }, [dispute, user?.id, isRenter, compensationType, counterCompensationType]);

  // State for accept confirmation modal
  const [acceptConfirmDialogOpen, setAcceptConfirmDialogOpen] = useState(false);
  const [acceptError, setAcceptError] = useState('');
  
  // State for 3DS authentication
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authClientSecret, setAuthClientSecret] = useState(null);
  const [authPaymentMethodId, setAuthPaymentMethodId] = useState(null);
  const [authPaymentIntentId, setAuthPaymentIntentId] = useState(null);
  const [authProcessing, setAuthProcessing] = useState(false);
  const [post3dsRefreshing, setPost3dsRefreshing] = useState(false);
  
  // 🆕 State for admin payment auth (système isolé)
  const [adminAuthRequest, setAdminAuthRequest] = useState(null);
  const [adminAuthModalOpen, setAdminAuthModalOpen] = useState(false);
  const [adminAuthLoading, setAdminAuthLoading] = useState(false);
  
  // State for escalate to admin modal
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalateError, setEscalateError] = useState(null);
  
  // 🆕 State for damage compensation payout
  const [damageCompensationPayout, setDamageCompensationPayout] = useState(null);
  const [loadingPayout, setLoadingPayout] = useState(false);
  
  // 🆕 State for renter refund payment
  const [renterRefundPayment, setRenterRefundPayment] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  
  // 🆕 State for applied penalties
  const [appliedPenalties, setAppliedPenalties] = useState([]);
  const [visiblePenaltiesCount, setVisiblePenaltiesCount] = useState(3); // Nombre initial de pénalités visibles
  const PENALTIES_PER_PAGE = 3; // Nombre de pénalités à charger à chaque fois

  // 🆕 Ban depuis le litige (admin)
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [banForm, setBanForm] = useState({
    target: 'renter', // 'renter' | 'owner'
    userId: '',
    banType: 'temporary',
    durationDays: 7,
    reason: ''
  });
  const [banSubmitting, setBanSubmitting] = useState(false);

  // 🆕 Résumé du ban actif (pour l'accordéon dans la box ban)
  // On gère les 2 parties (owner + renter) pour éviter un affichage "un seul user pas banni".
  const [banSummary, setBanSummary] = useState({
    loading: false,
    owner: { userId: null, ban: null },
    renter: { userId: null, ban: null },
    error: null
  });

  // 🆕 Débannissement depuis le litige (admin)
  const [unbanDialogOpen, setUnbanDialogOpen] = useState(false);
  const [unbanForm, setUnbanForm] = useState({
    userId: '',
    userName: '',
    reason: ''
  });
  const [unbanUserInfo, setUnbanUserInfo] = useState(null);
  const [unbanSubmitting, setUnbanSubmitting] = useState(false);

  // Infos utilisateur pour le dialog de ban
  const [banUserInfo, setBanUserInfo] = useState(null);

  // 🆕 Statut de ban pour les parties (owner/renter) - utilise le nouvel endpoint
  const [partiesBanStatus, setPartiesBanStatus] = useState({
    loading: false,
    owner: { userId: null, ban: null },
    renter: { userId: null, ban: null },
    error: null
  });

  // États pour les accordéons des sections admin
  const [adminDecisionExpanded, setAdminDecisionExpanded] = useState(false);
  const [financialAnalysisExpanded, setFinancialAnalysisExpanded] = useState(false);
  const [penaltyFormExpanded, setPenaltyFormExpanded] = useState(false);
  const [penaltySummaryExpanded, setPenaltySummaryExpanded] = useState(false);

  const extractReservationData = (response) => {    
    if (!response) return null;
    if (response?.data?.data?.reservation) return response.data.data.reservation;
    if (response?.data?.data) return response.data.data;
    if (response?.data) return response.data;
    return response;
  };

  // 🆕 Fonction pour charger le statut de ban via le nouvel endpoint (pour owner/renter)
  const fetchPartiesBanStatusForParties = async (disputeId) => {
    try {
      setPartiesBanStatus(prev => ({
        ...prev,
        loading: true,
        error: null
      }));

      const response = await disputeAdminService.getPartiesBanStatus(disputeId);
      const data = response?.data || response;

      setPartiesBanStatus({
        loading: false,
        owner: data.owner || { userId: null, ban: null },
        renter: data.renter || { userId: null, ban: null },
        error: null
      });
    } catch (err) {
      console.error('Failed to load parties ban status:', err);
      setPartiesBanStatus(prev => ({ 
        ...prev, 
        loading: false, 
        error: err?.message || 'Failed to load ban status'
      }));
    }
  };

  const fetchDispute = async ({ withRelated = true, force = false } = {}) => {
    const now = Date.now();
    const minIntervalMs = 1200;

    // ✅ FIX P1-3: Circuit-breaker - bloquer temporairement si trop d'erreurs consécutives
    if (fetchDisputeCircuitOpenRef.current) {
      console.warn('[fetchDispute] Circuit breaker OPEN - trop d\'erreurs, blocage temporaire');
      return;
    }

    // Coalesce bursts (socket + polling + actions) to reduce 429.
    if (!force && now - lastFetchDisputeAtRef.current < minIntervalMs) {
      fetchDisputeQueuedWithRelatedRef.current = fetchDisputeQueuedWithRelatedRef.current || withRelated;
      fetchDisputeQueuedRef.current = true;
      if (!fetchDisputeQueuedTimerRef.current) {
        const delay = Math.max(0, minIntervalMs - (now - lastFetchDisputeAtRef.current));
        fetchDisputeQueuedTimerRef.current = setTimeout(() => {
          fetchDisputeQueuedTimerRef.current = null;
          const queuedWithRelated = fetchDisputeQueuedWithRelatedRef.current;
          fetchDisputeQueuedRef.current = false;
          fetchDisputeQueuedWithRelatedRef.current = false;
          fetchDispute({ withRelated: queuedWithRelated, force: true });
        }, delay);
      }
      return;
    }

    if (fetchDisputeInFlightRef.current) {
      fetchDisputeQueuedWithRelatedRef.current = fetchDisputeQueuedWithRelatedRef.current || withRelated;
      fetchDisputeQueuedRef.current = true;
      return;
    }

    try {
      fetchDisputeInFlightRef.current = true;
      lastFetchDisputeAtRef.current = Date.now();

      // Don't set loading to true to avoid flashing if refreshing
      const response = await disputeAdminService.get(disputeId);
      setDispute(response?.data);
      
      // ✅ FIX P1-3: Reset error counter on success
      fetchDisputeErrorCountRef.current = 0;

      // ✅ FIX P1-1: CRITIQUE - Ne JAMAIS faire early return avant finally
      // Sinon fetchDisputeInFlightRef reste bloqué à true → DEADLOCK
      if (!withRelated) {
        // Skip related data fetching mais TOUJOURS passer par finally
        setLoading(false);
        return;
      }
      
      // Charger la réservation complète avec tous les payments
      const resId = response?.data?.reservation_id || response?.data?.reservation?.id;
      if (resId) {
        try {
          const reservationResponse = await reservationService.getReservationById(resId);
          const reservationData = extractReservationData(reservationResponse);
          setFullReservation(reservationData);
        } catch (resErr) {
          console.warn('[DisputeDetail] Failed to fetch full reservation (non-blocking)', { reservationId: resId, error: resErr });
          // Non bloquant - continuer même si la réservation complète ne charge pas
        }
      }
      
      // 🆕 Charger les pénalités appliquées pour ce dispute
      await fetchAppliedPenalties(disputeId);

      // 🆕 Charger les bans des 2 parties (si admin)
      if (user?.roles?.includes('admin')) {
        const d = response?.data;
        const ownerId = d?.reservation?.listing?.ownerId || d?.reservation?.listing?.owner?.id || d?.reservation?.listing?.owner_id || null;
        const renterId = d?.reservation?.renter_id || d?.reservation?.renter?.id || null;
        await fetchPartiesBanSummary({ ownerId, renterId });
      } else {
        // Pour owner/renter : charger via le nouvel endpoint
        await fetchPartiesBanStatusForParties(disputeId);
      }
    } catch (err) {
      console.error('Failed to fetch dispute:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to load dispute');
      
      // ✅ FIX P1-3: Circuit-breaker - compter erreurs consécutives
      fetchDisputeErrorCountRef.current += 1;
      
      // Si 5 erreurs consécutives, ouvrir circuit pendant 30s
      if (fetchDisputeErrorCountRef.current >= 5) {
        console.error('[fetchDispute] Circuit breaker OUVERT - trop d\'erreurs (5+)');
        fetchDisputeCircuitOpenRef.current = true;
        
        setTimeout(() => {
          console.log('[fetchDispute] Circuit breaker FERMÉ - réessai autorisé');
          fetchDisputeCircuitOpenRef.current = false;
          fetchDisputeErrorCountRef.current = 0;
        }, 30000);
      }
    } finally {
      fetchDisputeInFlightRef.current = false;
      
      // ✅ FIX P1-3: Circuit-breaker sur le flush - limiter récursions
      // Ne flush QUE si pas d'erreur récente (circuit fermé)
      if (fetchDisputeQueuedRef.current && !fetchDisputeCircuitOpenRef.current) {
        fetchDisputeQueuedRef.current = false;
        const queuedWithRelated = fetchDisputeQueuedWithRelatedRef.current;
        fetchDisputeQueuedWithRelatedRef.current = false;
        // Force to bypass throttle window.
        fetchDispute({ withRelated: queuedWithRelated, force: true });
      } else if (fetchDisputeQueuedRef.current && fetchDisputeCircuitOpenRef.current) {
        // Circuit ouvert - annuler la queue
        console.warn('[fetchDispute] Queue annulée - circuit breaker OPEN');
        fetchDisputeQueuedRef.current = false;
        fetchDisputeQueuedWithRelatedRef.current = false;
      }
      
      setLoading(false);
    }
  };
  
  // 🆕 Fonction pour charger les pénalités appliquées
  const fetchAppliedPenalties = async (dispId) => {
    try {
      // Utiliser l'API penalties pour récupérer les pénalités de ce dispute
      const response = await disputeAdminService.getPenalties(dispId);
      const penalties = response?.data?.penalties || response?.data || [];
      // Filtrer pour ne garder que les pénalités appliquées ou collectées
      const applied = penalties.filter(p => 
        p.status === 'applied' || p.status === 'collected' || p.status === 'pending_collection'
      );
      setAppliedPenalties(applied);
    } catch (err) {
      console.warn('Failed to fetch applied penalties (non-blocking)', err);
      // Non bloquant
      setAppliedPenalties([]);
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // After 3DS, backend may need a few seconds (webhook) to mark dispute as resolved.
  // This polling ensures the page becomes consistent without manual refresh.
  const pollDisputeUntilFinal = async ({
    timeoutMs = 20000,
    intervalMs = 2000,
    finalStatuses = ['resolved', 'closed']
  } = {}) => {
    const start = Date.now();

    if (post3dsPollingRef.current) return null;
    
    // ✅ FIX P2-5: Robustesse - garantir cleanup même en cas d'exception non-catchée
    try {
      post3dsPollingRef.current = true;
      setPost3dsRefreshing(true);

      let currentIntervalMs = intervalMs;
      while (isMountedRef.current && Date.now() - start < timeoutMs) {
        try {
          const response = await disputeAdminService.get(disputeId);
          const nextDispute = response?.data;
          if (nextDispute) {
            setDispute(nextDispute);
            if (finalStatuses.includes(nextDispute.status)) {
              return nextDispute;
            }
          }

          // Reset backoff on success
          currentIntervalMs = intervalMs;
        } catch (e) {
          const status = e?.response?.status;
          if (status === 429) {
            const retryAfterHeader = e?.response?.headers?.['retry-after'];
            let retryAfterMs = 0;
            if (retryAfterHeader) {
              const asNumber = Number(retryAfterHeader);
              if (!Number.isNaN(asNumber) && asNumber > 0) {
                retryAfterMs = asNumber * 1000;
              } else {
                const asDate = Date.parse(retryAfterHeader);
                if (!Number.isNaN(asDate)) {
                  retryAfterMs = Math.max(0, asDate - Date.now());
                }
              }
            }

            // Backoff with cap + a bit of jitter
            const base = Math.max(currentIntervalMs, 2500);
            const backoff = Math.min(12000, Math.floor(base * 1.5));
            const jitter = Math.floor(Math.random() * 400);
            currentIntervalMs = Math.max(backoff, retryAfterMs) + jitter;
          }
          // ignore transient failures during polling
        }

        await sleep(currentIntervalMs);
      }

      return null;
    } catch (unexpectedError) {
      // ✅ FIX P2-5: Capturer toute exception inattendue pour éviter refs bloquées
      console.error('[pollDisputeUntilFinal] Unexpected error:', unexpectedError);
      return null;
    } finally {
      // ✅ FIX P2-5: TOUJOURS cleanup, même si exception non-catchée
      setPost3dsRefreshing(false);
      post3dsPollingRef.current = false;
    }
  };

  useEffect(() => {
    if (disputeId) {
      setLoading(true);
      fetchDispute({ withRelated: true, force: true });
    }
  }, [disputeId]);

  // Real-time refresh (best-effort): if backend emits dispute events
  useEffect(() => {
    if (!disputeId) return;
    let active = true;

    const setup = async () => {
      try {
        await socketService.connect();
        socketService.on('dispute_resolved', async (payload) => {
          if (!active) return;
          const incomingId = payload?.id || payload?.dispute_id || payload?.dispute?.id;
          if (incomingId && String(incomingId) === String(disputeId)) {
            await fetchDispute({ withRelated: false });
          }
        });
      } catch (e) {
        // Socket is optional; polling handles consistency.
      }
    };

    setup();

    return () => {
      active = false;
      try {
        socketService.off('dispute_resolved');
      } catch (e) {
        // ignore
      }
    };
  }, [disputeId]);

  // Charger adminAuthRequest (même après résolution) pour que l'encart ne disparaisse pas
  useEffect(() => {
    if (!disputeId || !dispute || !user) return;

    // ✅ FIX P1-2: CRITIQUE - Retirer guard admin pour permettre aux renters de voir le banner 3DS
    // Le banner renter (ligne ~1587) dépend de adminAuthRequest → doit être chargé pour TOUS
    // L'affichage conditionnel est géré dans le JSX, pas ici
    // if (!user?.roles?.includes('admin')) return; // ❌ RETIRÉ - causait régression 3DS

    if (adminAuthInFlightRef.current) return;

    // Avoid refetching on every dispute object change; status changes are enough.
    // (Dependencies below reflect this.)

    adminAuthInFlightRef.current = true;

    setAdminAuthLoading(true);
    adminPaymentAuthService.getByDisputeId(disputeId)
      .then(authReq => {
        setAdminAuthRequest(authReq);
        setAdminAuthLoading(false);
        adminAuthInFlightRef.current = false;
      })
      .catch(err => {
        // 404 = pas de 3DS requis, OK. Sinon: log l'erreur pour debug.
        const status = err?.response?.status;
        if (status && status !== 404) {
          console.warn('[AdminPaymentAuth] Failed to fetch auth request', {
            status,
            message: err?.message,
            data: err?.response?.data
          });
        } else {
          console.log('[AdminPaymentAuth] No auth request found (expected if no 3DS required)');
        }
        setAdminAuthRequest(null);
        setAdminAuthLoading(false);
        adminAuthInFlightRef.current = false;
      });
  }, [disputeId, dispute?.status, user?.id]);

  // 🆕 Charger le payout de compensation de dommages si l'utilisateur est le propriétaire et le litige est résolu en sa faveur
  useEffect(() => {
    const loadDamageCompensationPayout = async () => {
      console.log('[DamageCompensationPayout] Starting load check', {
        hasDispute: !!dispute,
        hasUser: !!user?.id,
        hasReservationId: !!dispute?.reservation_id,
        disputeStatus: dispute?.status
      });

      // Ne rien faire si :
      // - Pas de dispute ou pas d'utilisateur
      // - Le litige n'est pas résolu ou fermé
      // - Pas de réservation associée
      if (!dispute || !user?.id || !dispute.reservation_id) {
        console.log('[DamageCompensationPayout] Missing basic requirements');
        setDamageCompensationPayout(null);
        return;
      }

      if (!['resolved', 'closed'].includes(dispute.status)) {
        console.log('[DamageCompensationPayout] Dispute status not resolved/closed:', dispute.status);
        setDamageCompensationPayout(null);
        return;
      }

      // Vérifier si l'utilisateur est le propriétaire
      const ownerId = dispute.reservation?.listing?.ownerId || 
                      dispute.reservation?.listing?.owner?.id || 
                      dispute.reservation?.listing?.owner_id;
      
      console.log('[DamageCompensationPayout] Owner check', {
        ownerId,
        userId: user.id,
        isOwner: ownerId === user.id,
        listing: dispute.reservation?.listing
      });
      
      if (ownerId !== user.id) {
        console.log('[DamageCompensationPayout] User is not the owner');
        setDamageCompensationPayout(null);
        return;
      }

      // Vérifier si le litige est résolu en faveur du propriétaire
      const adminDecision = dispute.metadata?.admin_decision?.decision;
      const compensationType = dispute.compensation_type || dispute.metadata?.admin_decision?.compensationType;
      
      console.log('[DamageCompensationPayout] Checking favor owner', {
        adminDecision,
        compensationType,
        metadata: dispute.metadata
      });
      
      const isFavorOwner = (
        adminDecision === 'favor_owner' ||
        compensationType === 'owner_compensation' ||
        (adminDecision === 'shared' && compensationType === 'owner_compensation')
      );

      if (!isFavorOwner) {
        console.log('[DamageCompensationPayout] Not in favor of owner');
        setDamageCompensationPayout(null);
        return;
      }

      // Charger le payout de compensation
      try {
        console.log('[DamageCompensationPayout] Loading payout for reservation:', dispute.reservation_id);
        setLoadingPayout(true);
        const payout = await payoutService.getDamageCompensationByReservation(
          dispute.reservation_id
        );
        console.log('[DamageCompensationPayout] Payout loaded successfully:', payout);
        setDamageCompensationPayout(payout);
      } catch (error) {
        console.error('[DamageCompensationPayout] Failed to load payout', {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setDamageCompensationPayout(null);
      } finally {
        setLoadingPayout(false);
      }
    };

    loadDamageCompensationPayout();
  }, [dispute, user?.id]);

  // 🆕 Charger le payment de remboursement si l'utilisateur est le locataire et le litige est résolu en sa faveur
  useEffect(() => {
    const loadRenterRefundPayment = async () => {
      console.log('[RenterRefundPayment] Starting load check', {
        hasDispute: !!dispute,
        hasUser: !!user?.id,
        hasReservationId: !!dispute?.reservation_id,
        disputeStatus: dispute?.status
      });

      // Ne rien faire si :
      // - Pas de dispute ou pas d'utilisateur
      // - Le litige n'est pas résolu ou fermé
      // - Pas de réservation associée
      if (!dispute || !user?.id || !dispute.reservation_id) {
        console.log('[RenterRefundPayment] Missing basic requirements');
        setRenterRefundPayment(null);
        return;
      }

      if (!['resolved', 'closed'].includes(dispute.status)) {
        console.log('[RenterRefundPayment] Dispute status not resolved/closed:', dispute.status);
        setRenterRefundPayment(null);
        return;
      }

      // Vérifier si l'utilisateur est le locataire
      const renterId = dispute.reservation?.renter_id || 
                       dispute.reservation?.renter?.id;
      
      console.log('[RenterRefundPayment] Renter check', {
        renterId,
        userId: user.id,
        isRenter: renterId === user.id,
        reservation: dispute.reservation
      });
      
      if (renterId !== user.id) {
        console.log('[RenterRefundPayment] User is not the renter');
        setRenterRefundPayment(null);
        return;
      }

      // Vérifier si le litige est résolu en faveur du locataire
      const adminDecision = dispute.metadata?.admin_decision?.decision;
      const compensationType = dispute.compensation_type || dispute.metadata?.admin_decision?.compensationType;
      
      console.log('[RenterRefundPayment] Checking favor renter', {
        adminDecision,
        compensationType,
        metadata: dispute.metadata
      });
      
      const isFavorRenter = (
        adminDecision === 'favor_renter' ||
        compensationType === 'renter_refund' ||
        compensationType === 'reservation_cancellation' ||
        (adminDecision === 'shared' && compensationType === 'renter_refund')
      );

      if (!isFavorRenter) {
        console.log('[RenterRefundPayment] Not in favor of renter');
        setRenterRefundPayment(null);
        return;
      }

      // Charger le payment de remboursement
      try {
        console.log('[RenterRefundPayment] Loading refund payment for reservation:', dispute.reservation_id);
        setLoadingRefund(true);
        const payment = await paymentService.getRenterRefundByReservation(
          dispute.reservation_id
        );
        console.log('[RenterRefundPayment] Payment loaded successfully:', payment);
        setRenterRefundPayment(payment);
      } catch (error) {
        console.error('[RenterRefundPayment] Failed to load refund payment', {
          error: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setRenterRefundPayment(null);
      } finally {
        setLoadingRefund(false);
      }
    };

    loadRenterRefundPayment();
  }, [dispute, user?.id]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    try {
      setSending(true);
      
      const formData = new FormData();
      formData.append('event_type', 'message');
      if (newMessage.trim()) {
        formData.append('message', newMessage);
      }
      
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });

      await disputeAdminService.addEvent(disputeId, formData);

      setNewMessage('');
      setAttachments([]);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await fetchDispute();
    } catch (err) {
      console.error('Failed to send message:', err);
      // Show error notification
    } finally {
      setSending(false);
    }
  };

  const handleActionClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
  };

  // 🔄 Helper: Valider le montant minimum pour compensation Stripe
  const validateStripeCompensationAmount = async (amount, compensationType, handlerName) => {
    const isOwnerCompensation = compensationType === 'owner_compensation';
    const paymentMethod = dispute?.reservation?.payments?.[0]?.payment_method?.toLowerCase();
    const isStripPayment = paymentMethod === 'stripe';

    console.log(`[${handlerName}] 📋 Pre-validation check:`, {
      compensationType,
      isOwnerCompensation,
      paymentMethod,
      isStripPayment,
      amount
    });

    // Si c'est une compensation au propriétaire via Stripe, vérifier le montant minimum
    if (isOwnerCompensation && isStripPayment && amount > 0) {
      const reservationCurrency = dispute?.reservation?.currency || 'EUR';
      
      console.log(`[${handlerName}] 💱 Checking Stripe minimum ($10 USD):`, {
        amount,
        currency: reservationCurrency
      });

      const conversionResult = await currencyConversionService.convertToUSD(amount, reservationCurrency);
      
      console.log(`[${handlerName}] 🔄 Conversion result:`, conversionResult);

      if (!conversionResult.success) {
        // Erreur de conversion
        const errorMsg = conversionResult.error || 'Erreur lors de la vérification du montant';
        console.log(`[${handlerName}] ❌ Conversion failed:`, errorMsg);
        throw new Error(errorMsg);
      }

      if (!conversionResult.meetsMinimum) {
        // Montant trop petit
        const minAmountMsg = t(
          'amicable.stripMinimumError',
          `⚠️ Montant minimum: Le paiement par Stripe requiert un montant minimum de 10 USD (environ 0,50€). Vous avez proposé ${conversionResult.equivalentInUSD} USD. Veuillez augmenter le montant ou choisir une autre type de compensation (annulation par exemple).`
        );
        console.log(`[${handlerName}] ❌ Amount too small:`, conversionResult);
        throw new Error(minAmountMsg);
      }

      console.log(`[${handlerName}] ✅ Amount validation passed:`, {
        amountInUSD: conversionResult.amountInUSD,
        meetsMinimum: true
      });
    }

    return true; // Validation réussie
  };

  const openActionDialog = (type) => {
    setActionType(type);
    setActionDialogOpen(true);
    handleActionClose();
  };

  const handleActionSubmit = async () => {
    try {
      setSending(true);
      const payload = {
        status: actionType === 'resolve' ? 'resolved' : actionType === 'close' ? 'closed' : 'escalated',
        message: actionNote
      };

      if (actionType === 'resolve' && compensationAmount) {
        payload.compensation_amount = parseFloat(compensationAmount);
      }

      await disputeAdminService.updateStatus(disputeId, payload);
      setActionDialogOpen(false);
      setActionNote('');
      setCompensationAmount('');
      await fetchDispute();
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setSending(false);
    }
  };

  // 🔄 Générer un message de proposition formaté avec traduction
  const generateProposalFormattedMessage = (compensationType, amount, userReason) => {
    const proposalId = (dispute?.metadata?.proposals?.length || 0) + 1;
    const isCancellation = compensationType === 'reservation_cancellation';
    const typeToSend = sanitizeCompensationType(compensationType);
    const currency = getCurrencySymbol(dispute?.reservation?.currency || 'EUR');

    // Déterminer le label du type de compensation
    let proposalDescription;
    if (isCancellation) {
      proposalDescription = t('amicable.cancellation', 'annulation de la réservation');
    } else {
      const amountFormatted = parseFloat(amount).toFixed(2);
      const typeLabel = compensationType === 'owner_compensation'
        ? t('amicable.ownerCompensation', 'Dédommagement au propriétaire')
        : t('amicable.renterRefund', 'Remboursement au locataire');
      proposalDescription = `${amountFormatted} ${currency} - ${typeLabel}`;
    }

    // Format avec traduction: "Proposition #X: [description]. Raison: [userReason]"
    return t('amicable.proposalMessage', 'Proposition #{{proposalId}}: {{proposalDescription}}. Raison: {{reason}}', {
      proposalId,
      proposalDescription,
      reason: userReason
    });
  };

  const handleProposeResolution = async () => {
    try {
      setSending(true);
      setProposalError('');
      const typeToSend = sanitizeCompensationType(compensationType);
      const isCancellation = typeToSend === 'reservation_cancellation';
      const amountToSend = isCancellation ? 0 : parseFloat(proposalAmount);

      // Valider le montant minimum Stripe si applicable
      try {
        await validateStripeCompensationAmount(amountToSend, typeToSend, 'handleProposeResolution');
      } catch (validationError) {
        enqueueSnackbar(validationError.message, { variant: 'error' });
        setProposalError(validationError.message);
        setSending(false);
        return;
      }

      // 🔄 Générer le message formalisé avec raison
      const formattedMessage = generateProposalFormattedMessage(compensationType, proposalAmount, proposalMessage);

      // ✅ Validation passée, envoyer la proposition
      await disputeAdminService.proposeResolution(disputeId, {
        amount: amountToSend,
        message: formattedMessage,
        compensation_type: typeToSend // 🔄 Envoyer le type de compensation
      });
      setProposalDialogOpen(false);
      setProposalAmount('');
      setProposalMessage('');
      setCompensationType(defaultCompensationType); // Reset
      await fetchDispute();
    } catch (err) {
      console.error('Failed to propose resolution:', err);
      const errorMessage = err?.response?.data?.error || t('errors.proposeFailed', 'Impossible d\'envoyer la proposition.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setProposalError(errorMessage);
      // Rafraîchir pour afficher la proposition existante (ex: déjà en pending)
      await fetchDispute();
    } finally {
      setSending(false);
    }
  };

  const handleAcceptProposal = async () => {
    try {
      setSending(true);
      const response = await disputeAdminService.acceptResolution(disputeId);
      
      // Vérifier si une authentification 3DS est requise
      if (response?.data?.requires_authentication && response?.data?.authentication) {
        console.log('[3DS] Authentication required for dispute resolution', {
          client_secret: response.data.authentication.client_secret,
          payment_method_id: response.data.authentication.payment_method_id,
          payment_intent_id: response.data.authentication.payment_intent_id
        });
        
        // Fermer le dialogue de confirmation et ouvrir le modal 3DS
        setAcceptConfirmDialogOpen(false);
        setAuthClientSecret(response.data.authentication.client_secret);
        setAuthPaymentMethodId(response.data.authentication.payment_method_id);
        setAuthPaymentIntentId(response.data.authentication.payment_intent_id);
        setAuthModalOpen(true);
        return;
      }
      
      // Résolution réussie sans 3DS
      setAcceptConfirmDialogOpen(false);
      enqueueSnackbar(t('success.resolutionAccepted', 'Résolution acceptée avec succès'), { variant: 'success' });
      await fetchDispute();
    } catch (err) {
      console.error('Failed to accept resolution:', err);
      // Try to extract a useful message from the response
      let message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erreur lors de l\'acceptation de la proposition';
      // If backend returns a string containing JSON, try to parse the JSON part
      if (typeof message === 'string' && message.includes('{')) {
        const jsonPart = message.slice(message.indexOf('{'));
        try {
          const parsed = JSON.parse(jsonPart);
          if (parsed && parsed.error) {
            message = parsed.error;
          }
        } catch (e) {
          // ignore parse errors, keep original message
        }
      }
      
      // Fermer le dialogue même en cas d'erreur
      setAcceptConfirmDialogOpen(false);
      
      // Afficher un message adapté pour l'échec 3DS
      if (message.includes('3DS') || message.includes('authentication') || message.includes('authentification')) {
        enqueueSnackbar(
          'L\'authentification 3DS a échoué. Votre proposition reste valable et vous pouvez retenter.', 
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar(message, { variant: 'error' });
      }
      
      // Rafraîchir pour montrer que la proposition est toujours active
      await fetchDispute();
    } finally {
      setSending(false);
    }
  };
  
  // Handler pour confirmer l'authentification 3DS après validation
  const handleAuthenticationSuccess = async (paymentIntent) => {
    setAuthProcessing(true);
    try {
      console.log('[3DS] Confirming authentication with backend', {
        payment_intent_id: paymentIntent.id
      });
      
      // Appeler l'endpoint de confirmation
      await disputeAdminService.confirmDepositAuthentication(disputeId, paymentIntent.id);
      
      // Fermer le modal
      setAuthModalOpen(false);
      setAuthClientSecret(null);
      setAuthPaymentIntentId(null);
      setAuthProcessing(false);
      
      enqueueSnackbar(t('success.resolutionAccepted', 'Résolution acceptée avec succès'), { variant: 'success' });
      
      // Rafraîchir immédiatement
      await fetchDispute();

      // Poll briefly until the dispute actually becomes resolved/closed
      enqueueSnackbar(
        t('info.finalizingSettlement', 'Finalisation du règlement en cours…'),
        { variant: 'info' }
      );
      await pollDisputeUntilFinal();
    } catch (err) {
      console.error('[3DS] Failed to confirm authentication:', err);
      const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Erreur lors de la confirmation';
      enqueueSnackbar(message, { variant: 'error' });
      setAuthModalOpen(false);
      setAuthProcessing(false);
    }
  };
  
  // Handler pour gérer les erreurs d'authentification 3DS
  const handleAuthenticationError = async (error) => {
    console.error('[3DS] Authentication error:', error);
    const message = error?.message || 'Erreur lors de l\'authentification';
    
    // Appeler l'API de cancel pour enregistrer l'échec
    if (authPaymentIntentId && disputeId) {
      try {
        await disputeAdminService.cancelDepositAuthentication(disputeId, authPaymentIntentId, 'authentication_failed');
      } catch (cancelErr) {
        console.error('[3DS] Failed to cancel authentication:', cancelErr);
      }
    }
    
    enqueueSnackbar(
      'L\'authentification 3DS a échoué. Votre proposition reste valable et vous pouvez retenter.',
      { variant: 'warning' }
    );
    setAuthModalOpen(false);
    setAuthClientSecret(null);
    setAuthPaymentMethodId(null);
    setAuthPaymentIntentId(null);
    
    // Rafraîchir pour montrer que la proposition est toujours active
    await fetchDispute();
  };

  // Handler pour gérer l'abandon/annulation de l'authentification 3DS
  const handleAuthenticationCancel = async (paymentIntentId, disputeId, reason) => {
    console.log('[3DS] Cancel authentication', { paymentIntentId, disputeId, reason });
    
    try {
      await disputeAdminService.cancelDepositAuthentication(disputeId, paymentIntentId, reason);
      enqueueSnackbar(
        'Authentification annulée. Votre proposition reste valable et vous pouvez retenter.',
        { variant: 'info' }
      );
      await fetchDispute(); // Refresh pour voir l'événement et la proposition
    } catch (err) {
      console.error('[3DS] Failed to cancel authentication:', err);
    }
    
    // Fermer le modal
    setAuthModalOpen(false);
    setAuthClientSecret(null);
    setAuthPaymentMethodId(null);
    setAuthPaymentIntentId(null);
  };

  // Handler pour escalader le litige à l'admin
  const handleEscalateToAdmin = async () => {
    try {
      setSending(true);
      setEscalateError(null);
      await disputeAdminService.escalate(disputeId, {
        reason: escalateReason || 'Escalade demandée par une partie'
      });
      setEscalateDialogOpen(false);
      setEscalateReason('');
      setEscalateError(null);
      await fetchDispute();
    } catch (err) {
      console.error('Failed to escalate dispute:', err);
      const msg = err?.response?.data?.message || err?.message || t('escalate.error', 'Une erreur est survenue lors de l\'escalade.');
      setEscalateError(msg);
      // Rafraîchir le litige pour que la section propositions reflète l'état réel
      try { await fetchDispute(); } catch (_) {}
    } finally {
      setSending(false);
    }
  };

  const handleRejectProposal = async () => {
    try {
      setSending(true);
      // 🔄 Générer le message formalisé de rejet avec traduction
      const proposalId = dispute?.metadata?.proposals?.length || 0;
      const formattedMessage = t('amicable.rejectionMessage', {
        proposalId,
        reason: rejectReason
      });
      
      await disputeAdminService.rejectResolution(disputeId, formattedMessage);
      setRejectDialogOpen(false);
      setRejectReason('');
      await fetchDispute();
    } catch (err) {
      console.error('Failed to reject resolution:', err);
    } finally {
      setSending(false);
    }
  };

  // 🔄 Mapper les clés machine de types de compensation vers des labels traduits
  const getCompensationTypeLabel = (type) => {
    const compensationTypeMap = {
      'owner_compensation': t('amicable.ownerCompensation', 'Dédommagement au propriétaire'),
      'renter_refund': t('amicable.renterRefund', 'Remboursement au locataire'),
      'reservation_cancellation': t('amicable.cancellation', 'Annulation pure et simple')
    };
    return compensationTypeMap[type] || type;
  };

  // 🔄 Nouvelle méthode (master) qui gère admin_decision OU amicable_proposal
  const getResolutionDisplayText = () => {
    // Priorité: 1) Admin decision, 2) Amicable proposal
    const hasAdminDecision = !!dispute?.metadata?.admin_decision;
    const hasAmicableProposal = !!dispute?.metadata?.amicable_proposal;

    if (hasAdminDecision) {
      // Cas: Décision admin (arbitrage)
      const adminDec = dispute.metadata.admin_decision;
      const compensationType = adminDec.compensationType || adminDec.compensation_type;
      const amount = adminDec.amount;
      const currency = getCurrencySymbol(dispute?.reservation?.currency || 'EUR');

      let proposalDescription = '';
      
      // Si pas de compensationType = rejet par admin
      if (!compensationType) {
        proposalDescription = t('detail.adminDecision.rejection', 'Litige rejeté');
      } else if (compensationType === 'reservation_cancellation') {
        proposalDescription = t('detail.adminDecision.cancellation', 'Annulation de la réservation');
      } else {
        const formattedAmount = amount ? parseFloat(amount).toFixed(2) : null;
        const typeLabel = getCompensationTypeLabel(compensationType || '');
        proposalDescription = formattedAmount ? `${formattedAmount} ${currency} - ${typeLabel}` : `${typeLabel}`;
      }

      return t('detail.adminDecisionResolution', 'Décision administrative — {{proposalDescription}}', {
        proposalDescription
      });
    } else if (hasAmicableProposal) {
      // Cas: Résolution amiable (négociation acceptée)
      return getAmicableResolutionText();
    }

    return '';
  };

  // 🔄 Générer le texte affiché pour une résolution amiable (utilise la proposition stockée)
  const getAmicableResolutionText = () => {
    const prop = dispute?.metadata?.amicable_proposal || {};
    const currency = getCurrencySymbol(prop.currency) || getCurrencySymbol(dispute?.reservation?.currency) || getCurrencySymbol('EUR');
    let proposalDescription = '';
    if (prop.compensation_type === 'reservation_cancellation') {
      proposalDescription = t('amicable.cancellation', 'Annulation de la réservation');
    } else {
      const amount = dispute.compensation_amount ? parseFloat(dispute.compensation_amount).toFixed(2) : null;
      const typeLabel = getCompensationTypeLabel(prop.compensation_type || '');
      proposalDescription = `${typeLabel}`;
    }

    return t('detail.amicableAgreement', 'Accord amiable conclu — {{proposalDescription}}', {
      proposalDescription
    });
  };

  // 🔄 Générer le message pré-rempli pour la contre-proposition avec traduction
  const generateCounterProposalMessage = (translator) => {
    const currentProposal = dispute?.metadata?.amicable_proposal;
    const proposalId = (dispute?.metadata?.proposals?.length || 0) + 1;
    const currency = getCurrencySymbol(dispute?.reservation?.currency || 'EUR');

    // Déterminer quel template utiliser
    const typeChanged = counterCompensationType !== currentProposal?.compensation_type;
    const isCancellation = counterCompensationType === 'reservation_cancellation';

    let message = '';

    if (isCancellation) {
      // Use translation key: counterProposal.reservationCancellation
      message = translator('counterProposal.reservationCancellation', {
        id: proposalId
      });
    } else if (typeChanged) {
      // Use translation key: counterProposal.typeChanged
      const amount = parseFloat(counterAmount) || 0;
      const oldType = currentProposal?.compensation_type || 'unknown';
      const newType = counterCompensationType;
      // Translate type labels
      const oldTypeLabel = getCompensationTypeLabel(oldType);
      const newTypeLabel = getCompensationTypeLabel(newType);
      message = translator('counterProposal.typeChanged', {
        id: proposalId,
        amount,
        currency,
        oldType: oldTypeLabel,
        newType: newTypeLabel
      });
    } else {
      // Use translation key: counterProposal.basic
      const amount = parseFloat(counterAmount) || 0;
      const previousAmount = currentProposal?.amount || 0;
      message = translator('counterProposal.basic', {
        id: proposalId,
        amount,
        currency,
        previousAmount
      });
    }

    return message;
  };

  // 🔄 useEffect pour auto-générer le message quand le type ou montant change
  useEffect(() => {
    if (counterProposalDialogOpen) {
      const generatedMessage = generateCounterProposalMessage(t);
      setCounterMessage(generatedMessage);
    }
  }, [counterCompensationType, counterAmount, counterProposalDialogOpen, dispute?.metadata?.amicable_proposal, t]);

  const handleCounterProposal = async () => {
    try {
      setSending(true);
      setCounterError('');
      const typeToSend = sanitizeCompensationType(counterCompensationType);
      const isCancellation = typeToSend === 'reservation_cancellation';
      const amountToSend = isCancellation ? 0 : parseFloat(counterAmount);

      // Valider le montant minimum Stripe si applicable
      try {
        await validateStripeCompensationAmount(amountToSend, typeToSend, 'handleCounterProposal');
      } catch (validationError) {
        enqueueSnackbar(validationError.message, { variant: 'error' });
        setCounterError(validationError.message);
        setSending(false);
        return;
      }

      // ✅ Validation passée, envoyer la contre-proposition
      await disputeAdminService.counterProposal(disputeId, {
        amount: amountToSend,
        compensation_type: typeToSend, // 🔄 Envoi du type de compensation
        message: counterMessage,
        counterReason: 'Contre-proposition'
      });
      setCounterProposalDialogOpen(false);
      setCounterAmount('');
      setCounterMessage('');
      setCounterCompensationType(defaultCompensationType); // Reset au type par défaut
      await fetchDispute();
    } catch (err) {
      console.error('Failed to send counter-proposal:', err);
      const errorMessage = err?.response?.data?.error || t('errors.counterFailed', 'Impossible d\'envoyer la contre-proposition.');
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setCounterError(errorMessage);
      await fetchDispute();
    } finally {
      setSending(false);
    }
  };

  const fetchSingleBanSummary = async (userId) => {
    if (!userId) return { userId: null, ban: null };
    const response = await restrictionAdminService.getUserRestrictionSummary(userId);
    return {
      userId,
      ban: response?.data?.ban || null
    };
  };

  const fetchPartiesBanSummary = async ({ ownerId, renterId }) => {
    try {
      setBanSummary(prev => ({
        ...prev,
        loading: true,
        error: null,
        owner: { userId: ownerId || null, ban: prev.owner?.ban || null },
        renter: { userId: renterId || null, ban: prev.renter?.ban || null }
      }));

      const [owner, renter] = await Promise.all([
        fetchSingleBanSummary(ownerId),
        fetchSingleBanSummary(renterId)
      ]);

      setBanSummary(prev => ({
        ...prev,
        loading: false,
        owner,
        renter
      }));
    } catch (err) {
      setBanSummary(prev => ({ ...prev, loading: false, error: err?.message || 'Failed to load ban summary' }));
    }
  };

  const getDisputePartyIds = () => {
    const ownerId =
      dispute?.reservation?.listing?.ownerId ||
      dispute?.reservation?.listing?.owner?.id ||
      dispute?.reservation?.listing?.owner_id ||
      '';

    const renterId =
      dispute?.reservation?.renter_id ||
      dispute?.reservation?.renter?.id ||
      '';

    return { ownerId, renterId };
  };

  const openBanDialog = async () => {
    const { ownerId, renterId } = getDisputePartyIds();

    // Par défaut: renter si présent, sinon owner.
    const defaultTarget = renterId ? 'renter' : 'owner';
    const defaultUserId = defaultTarget === 'renter' ? renterId : ownerId;

    setBanForm(prev => ({
      ...prev,
      target: defaultTarget,
      userId: defaultUserId
    }));

    // Charger les infos utilisateur
    if (defaultUserId) {
      try {
        const response = await restrictionAdminService.getUserBasicInfo(defaultUserId);
        setBanUserInfo(response.data || response);
      } catch (err) {
        console.error('Failed to load user info for ban:', err);
        setBanUserInfo(null);
      }
    }

    setBanDialogOpen(true);
  };

  const handleSubmitBan = async () => {
    if (!banForm.userId || !banForm.reason?.trim()) return;

    // Guard: empêcher de bannir un user déjà banni
    const alreadyBanned =
      (banForm.target === 'owner' && banSummary.owner?.userId === banForm.userId && banSummary.owner?.ban?.isBanned) ||
      (banForm.target === 'renter' && banSummary.renter?.userId === banForm.userId && banSummary.renter?.ban?.isBanned);

    if (alreadyBanned) {
      enqueueSnackbar(t('ban.alreadyBanned', 'Cet utilisateur est déjà banni. Débannissez-le avant d’appliquer un nouveau ban.'), { variant: 'warning' });
      return;
    }

    try {
      setBanSubmitting(true);

      const disputeSourceId = dispute.dispute_id || dispute.id;

      const metadata = {
        source: 'dispute',
        dispute_id: disputeSourceId,
        dispute_title: dispute.title || null,
        reservation_id: dispute.reservation_id || null
      };

      // Lien robuste + indexable : source_type/source_id
      const sourceType = 'dispute';
      const sourceId = disputeSourceId;

      if (banForm.banType === 'permanent') {
        await restrictionAdminService.createPermanentBan({
          userId: banForm.userId,
          reason: banForm.reason.trim(),
          metadata,
          sourceType,
          sourceId
        });
      } else {
        await restrictionAdminService.createTemporaryBan({
          userId: banForm.userId,
          durationDays: parseInt(banForm.durationDays, 10),
          reason: banForm.reason.trim(),
          metadata,
          sourceType,
          sourceId
        });
      }

      // Tracer dans le litige via une note système (best-effort)
      try {
        const formData = new FormData();
        formData.append('event_type', 'system_note');
        const banTypeLabel = banForm.banType === 'permanent' ? t('ban.permanent') : `${t('ban.temporary')} (${banForm.durationDays}j)`;
        const messageText = t('ban.systemNoteBanApplied')
          .replace('{{type}}', banTypeLabel)
          .replace('{{userId}}', banForm.userId)
          .replace('{{reason}}', banForm.reason.trim());
        formData.append('message', messageText);
        await disputeAdminService.addEvent(disputeId, formData);
      } catch (e) {
        // non bloquant
      }

      enqueueSnackbar(t('ban.banAppliedSuccess'), { variant: 'success' });
      setBanDialogOpen(false);
      setBanForm({ target: 'renter', userId: '', banType: 'temporary', durationDays: 7, reason: '' });

      // Rafraîchir le résumé des bans des 2 parties pour l'accordéon
      const { ownerId, renterId } = getDisputePartyIds();
      await fetchPartiesBanSummary({ ownerId, renterId });
      await fetchDispute();

    } catch (err) {
      console.error('Failed to apply ban from dispute:', err);
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Failed to apply ban from dispute:', { variant: 'error' });
    } finally {
      setBanSubmitting(false);
    }
  };

  // 🆕 Débannir un utilisateur depuis le litige
  const handleOpenUnbanDialog = async (userId, userName) => {
    try {
      setUnbanForm({ userId, userName, reason: '' });
      
      // Charger les infos utilisateur (notamment la langue)
      const response = await restrictionAdminService.getUserBasicInfo(userId);
      setUnbanUserInfo(response.data || response);
      
      setUnbanDialogOpen(true);
    } catch (err) {
      console.error('Failed to load user info for unban:', err);
      enqueueSnackbar('Failed to load user info for unban:', { variant: 'error' });
    }
  };

  const handleUnbanUser = async () => {
    if (!unbanForm.userId || !unbanForm.reason?.trim()) {
      enqueueSnackbar(t('ban.unbanReasonRequired', 'Veuillez saisir une raison pour le débannissement'), { variant: 'warning' });
      return;
    }

    try {
      setUnbanSubmitting(true);

      await restrictionAdminService.unbanUser(unbanForm.userId, unbanForm.reason.trim());

      // Tracer dans le litige via une note système (best-effort)
      try {
        const formData = new FormData();
        formData.append('event_type', 'system_note');
        const messageText = t('ban.systemNoteUnbanned')
          .replace('{{userId}}', unbanForm.userId)
          .replace('{{reason}}', unbanForm.reason.trim());
        formData.append('message', messageText);
        await disputeAdminService.addEvent(disputeId, formData);
      } catch (e) {
        // non bloquant
      }

      enqueueSnackbar(t('ban.unbanSuccess', '✅ Utilisateur débanni avec succès'), { variant: 'success' });
      setUnbanDialogOpen(false);
      setUnbanForm({ userId: '', userName: '', reason: '' });
      setUnbanUserInfo(null);

      // Rafraîchir le résumé des bans
      const { ownerId, renterId } = getDisputePartyIds();
      await fetchPartiesBanSummary({ ownerId, renterId });
      await fetchDispute();

    } catch (err) {
      console.error('Failed to unban user from dispute:', err);
      enqueueSnackbar(err?.response?.data?.message || err?.message || t('ban.unbanFailed', 'Erreur lors du débannissement'), { variant: 'error' });
    } finally {
      setUnbanSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          {t('common.back', 'Retour')}
        </Button>
      </Container>
    );
  }

  if (!dispute) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="info">{t('detail.notFound', 'Litige introuvable')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {(authProcessing || post3dsRefreshing) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      )}
      
      {/* 🆕 Banner pour validation 3DS décision admin - Vue RENTER */}
      {adminAuthRequest && user?.id === adminAuthRequest.renter_id && (
        <AdminPaymentAuthBanner
          authRequest={adminAuthRequest}
          onValidateClick={() => setAdminAuthModalOpen(true)}
          onRefresh={fetchDispute}
          disputeStatus={dispute.status}
        />
      )}

      {/* 🆕 Banner pour validation 3DS décision admin - Vue ADMIN/OWNER */}
      {adminAuthRequest &&
       (user?.roles?.includes('admin') ||
        user?.id === dispute.reservation?.listing?.ownerId ||
        user?.id === dispute.reservation?.listing?.owner?.id ||
        user?.id === dispute.reservation?.listing?.owner_id) && (
        <AdminPaymentAuthAdminBanner
          authRequest={adminAuthRequest}
          isAdmin={user?.roles?.includes('admin')}
          disputeStatus={dispute.status}
        />
      )}
      
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          {t('common.back', 'Retour')}
        </Button>
        
        {/* Actions Menu - Only for Admins */}
        {user?.roles?.includes('admin') && dispute.status !== 'closed' && dispute.status !== 'resolved' && (
          <Box>
            <Button
              variant="contained"
              color="primary"
              endIcon={<ExpandMoreIcon />}
              onClick={handleActionClick}
            >
              {t('common.actions', 'Actions')}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleActionClose}
            >
              <MenuItem onClick={() => openActionDialog('resolve')}>
                <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                {t('actions.resolve', 'Résoudre le litige')}
              </MenuItem>
              <MenuItem onClick={() => openActionDialog('escalate')}>
                <ErrorIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                {t('actions.escalate', 'Escalader (Support)')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => openActionDialog('close')}>
                <CloseIcon fontSize="small" sx={{ mr: 1 }} />
                {t('actions.close', 'Fermer sans suite')}
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Stack>

      {/* En-tête avec titre et statut */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 56, height: 56 }}>
            <GavelIcon fontSize="large" />
          </Avatar>
          <Box flexGrow={1}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {dispute.title || t('detail.noTitle', 'Litige sans titre')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={getStatusLabel(dispute.status, t)}
                color={getStatusColor(dispute.status)}
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={`ID: #${dispute.id}`}
                variant="outlined"
                sx={{ color: 'white', borderColor: 'white' }}
              />
              <Chip
                label={format(new Date(dispute.created_at), 'dd MMMM yyyy', { locale: dateLocale })}
                variant="outlined"
                sx={{ color: 'white', borderColor: 'white' }}
              />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {/* Workflow visuel - Stepper horizontal */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventNoteIcon color="primary" />
          {t('detail.progressTitle', 'Progression du litige')}
        </Typography>
        <Stepper activeStep={getActiveStep(dispute.status)} alternativeLabel sx={{ mt: 3 }}>
          {getWorkflowSteps(t).map((step, index) => {
            const currentStep = getActiveStep(dispute.status);
            const isCompleted = index < currentStep;
            const isActive = index === currentStep;
            const isPending = index > currentStep;

            return (
              <Step key={step.key} completed={isCompleted}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      fontSize: '2rem',
                      color: isCompleted 
                        ? 'success.main' 
                        : isActive 
                        ? 'primary.main' 
                        : 'grey.400',
                      '& .MuiStepIcon-text': { 
                        fill: 'white',
                        fontWeight: 'bold'
                      }
                    }
                  }}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontWeight: isActive ? 'bold' : 'normal',
                      fontSize: isActive ? '1.1rem' : '0.9rem',
                      color: isCompleted 
                        ? 'success.dark' 
                        : isActive 
                        ? 'primary.main' 
                        : 'text.disabled'
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: isActive ? 'bold' : 'normal',
                        color: isCompleted 
                          ? 'success.dark' 
                          : isActive 
                          ? 'primary.main' 
                          : 'text.disabled'
                      }}
                    >
                      {step.label}
                    </Typography>
                    {isActive && (
                      <Chip 
                        label={t('detail.statusCurrent', 'EN COURS')}
                        size="small" 
                        color="primary" 
                        sx={{ 
                          mt: 1, 
                          fontWeight: 'bold',
                          animation: 'pulse 2s infinite'
                        }} 
                      />
                    )}
                    {isCompleted && (
                      <Chip 
                        label={t('detail.statusCompleted', '✓ TERMINÉ')}
                        size="small" 
                        color="success" 
                        variant="outlined"
                        sx={{ mt: 1 }} 
                      />
                    )}
                    {isPending && (
                      <Chip 
                        label={t('detail.statusPending', 'À VENIR')}
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          mt: 1, 
                          color: 'grey.500',
                          borderColor: 'grey.300'
                        }} 
                      />
                    )}
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Afficher la résolution uniquement si le litige est vraiment résolu (pas en cours de règlement) */}
      {dispute.resolution && (dispute.status === 'resolved' || dispute.status === 'closed') && (
        <Accordion sx={{ mb: 2, overflow: 'visible' }} expanded={resolutionOpen} onChange={(e, expanded) => setResolutionOpen(expanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="primary" />
              {t('detail.resolutionTitle', 'Résolution')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, overflow: 'visible' }}>
            <Card
              sx={{
                bgcolor: 'background.paper',
                borderLeft: 6,
                borderLeftColor: 'success.main',
                borderRadius: 2,
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 8px 20px rgba(0,0,0,0.6)'
                    : '0 14px 40px rgba(46,125,50,0.08)',
                p: 0
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body1" paragraph color="text.secondary">
                  {getResolutionDisplayText()}
                </Typography>

                {/* Récap annulation: remboursement locataire + libération caution */}
                {dispute.compensation_type === 'reservation_cancellation' && (() => {
                  const rentalPayments = dispute?.reservation?.payments || [];
                  const rentalOnlyPayments = rentalPayments.filter(p => p.payment_type === 'rental');
                  const totalRentalPaid = rentalOnlyPayments.reduce((sum, p) => {
                    const amount = parseFloat(p.amount || 0);
                    return sum + (isNaN(amount) ? 0 : amount);
                  }, 0);
                  const depositAmount = parseFloat(dispute?.reservation?.deposit?.amount || 0) || 0;
                  const resolutionCurrency = getCurrencySymbol(dispute?.reservation?.currency || dispute.compensation_currency || 'EUR');

                  const rentalPaymentMethods = Array.from(new Set(rentalOnlyPayments.map(p => (p.payment_method || '').toLowerCase()).filter(Boolean)));
                  const allRentalPaidInCash = rentalPaymentMethods.length > 0 && rentalPaymentMethods.every(m => m === 'cash');

                  if (allRentalPaidInCash) {
                    return (
                      <Alert severity="info" icon={<AccountBalanceIcon />} sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('compensation.cancellationCashSummary', 'Paiement en espèces — pas de remboursement en ligne')}
                        </Typography>
                        <Typography variant="body2">
                          {t('compensation.cancellationCashExplanation', 'Le locataire n\'a pas payé via la plateforme; aucun remboursement en ligne n\'a été effectué.')}
                        </Typography>
                        {depositAmount > 0 && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            • {t('compensation.depositReleased', 'Caution libérée')} : <strong>{depositAmount.toFixed(2)} {resolutionCurrency}</strong>
                          </Typography>
                        )}
                      </Alert>
                    );
                  }

                  return (
                    <Alert severity="info" icon={<AccountBalanceIcon />} sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        {t('compensation.cancellationSummary', 'Le locataire a été remboursé et la caution a été libérée :')}
                      </Typography>
                      <Typography variant="body2">
                        • {t('compensation.rentalRefund', 'Remboursement location')} : <strong>{totalRentalPaid.toFixed(2)} {resolutionCurrency}</strong>
                      </Typography>
                      {depositAmount > 0 && (
                        <Typography variant="body2">
                          • {t('compensation.depositReleased', 'Caution libérée')} : <strong>{depositAmount.toFixed(2)} {resolutionCurrency}</strong>
                        </Typography>
                      )}
                      {user?.id !== dispute.reservation?.listing?.ownerId && reservationPaymentMethod !== 'cash' && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          ℹ️ {t('compensation.refundNote', 'Le remboursement apparaîtra sur votre compte bancaire sous 3 à 10 jours ouvrables selon votre banque.')}
                        </Typography>
                      )}
                    </Alert>
                  );
                })()}

                {dispute.compensation_amount && (
                  <>
                    {parseFloat(dispute.compensation_amount || 0) > 0 && (
                      <Alert severity="info" icon={<AccountBalanceIcon />}>
                        <strong>{t('labels.compensation', 'Compensation')}:</strong> {dispute.compensation_amount} {getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}
                      </Alert>
                    )}
                    {/* Note sur les frais de traitement - affichée uniquement au propriétaire pour les compensations owner */}
                    {dispute.compensation_type === 'owner_compensation' && 
                     user?.id === dispute.reservation?.listing?.ownerId && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'block', 
                          mt: 1, 
                          fontStyle: 'italic',
                          pl: 1
                        }}
                      >
                        ℹ️ {t('compensation.processingFeeNote', 
                          'Le montant reçu peut être légèrement inférieur en raison des frais de traitement appliqués par notre partenaire de paiement. Ces frais standards sont indépendants de notre plateforme.'
                        )}
                      </Typography>
                    )}
                    {/* Résumé caution pour le locataire - affiché uniquement au renter pour les compensations owner */}
                    {dispute.compensation_type === 'owner_compensation' && 
                     user?.id === dispute.reservation?.renter_id && 
                     dispute.reservation?.deposit?.amount && (
                      <Alert 
                        severity="warning" 
                        sx={{ mt: 2 }}
                        icon={<SecurityIcon />}
                      >
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                          {t('compensation.depositSummaryTitle', '📋 Récapitulatif de votre caution')}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            • {t('compensation.originalDeposit', 'Caution initiale')} : <strong>{dispute.reservation.deposit.amount} {getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</strong>
                          </Typography>
                          <Typography variant="body2" color="error.dark">
                            • {t('compensation.deductedAmount', 'Montant retenu (dommages)')} : <strong>-{dispute.compensation_amount} {getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</strong>
                          </Typography>
                          <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
                            • {t('compensation.refundedAmount', 'Montant remboursé')} : <strong>{(parseFloat(dispute.reservation.deposit.amount) - parseFloat(dispute.compensation_amount)).toFixed(2)} {getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</strong>
                          </Typography>
                        </Box>
                        {reservationPaymentMethod !== 'cash' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                            {t('compensation.refundNote', 'Le remboursement sera effectué sur votre moyen de paiement initial sous 5 à 10 jours ouvrés.')}
                          </Typography>
                        )}
                      </Alert>
                    )}
                  </>
                )}
                {dispute.resolved_at && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    {t('detail.resolvedOn')} {format(new Date(dispute.resolved_at), 'dd/MM/yyyy HH:mm', { locale: dateLocale })}
                  </Typography>
                )}
              </CardContent>
            </Card>
            
            {/* 🆕 Afficher le payout de compensation de dommages si disponible (propriétaire uniquement) */}
            {loadingPayout && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {t('compensationPayout.loading', 'Chargement des informations de paiement...')}
                </Typography>
              </Box>
            )}
            
            {damageCompensationPayout && !loadingPayout && (
              <DamageCompensationPayoutSummary 
                payout={damageCompensationPayout}
                reservationCurrency={dispute?.reservation?.currency}
              />
            )}
            
            {/* 🆕 Afficher le remboursement si disponible (locataire uniquement) */}
            {loadingRefund && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {t('renterRefund.loading', 'Chargement des informations de remboursement...')}
                </Typography>
              </Box>
            )}
            
            {renterRefundPayment && !loadingRefund && (
              <RenterRefundPaymentSummary payment={renterRefundPayment} />
            )}
          </AccordionDetails>
        </Accordion>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        `}
      </style>

      {/* Dialog ban */}
      <Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('ban.applyDialogTitle', 'Appliquer un ban (lié au litige)')}</DialogTitle>
        <DialogContent>
          {banUserInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('ban.targetUser', 'Utilisateur ciblé')}
              </Typography>
              <Typography variant="body2">
                <strong>Nom :</strong> {banUserInfo.name}
              </Typography>
              <Typography variant="body2">
                <strong>Email :</strong> {banUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Langue :</strong> {banUserInfo.language || 'en'}
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            {t('ban.recordInfo', 'Ce ban sera enregistré côté restrictions avec metadata.dispute_id = {{id}}.', { id: dispute?.dispute_id || dispute?.id })}
          </Alert>

          <TextField
            fullWidth
            label={t('labels.userId', 'User ID')}
            value={banForm.userId}
            onChange={(e) => setBanForm(prev => ({ ...prev, userId: e.target.value }))}
            sx={{ mb: 2 }}
            helperText={t('ban.userIdHelper', 'Utilisateur à bannir (pré-rempli depuis la réservation)')}
          />

          {(banForm.target === 'owner'
            ? (banSummary.owner?.userId === banForm.userId && banSummary.owner?.ban?.isBanned)
            : (banSummary.renter?.userId === banForm.userId && banSummary.renter?.ban?.isBanned)) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('ban.alreadyBanned', 'Cet utilisateur est déjà banni. Débannissez-le avant d’appliquer un nouveau ban.')}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel>{t('ban.typeLabel', 'Type de ban')}</FormLabel>
            <RadioGroup
              row
              value={banForm.banType}
              onChange={(e) => setBanForm(prev => ({ ...prev, banType: e.target.value }))}
            >
              <FormControlLabel value="temporary" control={<Radio />} label={t('ban.temporary', 'Temporaire')} />
              <FormControlLabel value="permanent" control={<Radio />} label={t('ban.permanent', 'Permanent')} />
            </RadioGroup>
          </FormControl>

          {banForm.banType === 'temporary' && (
            <TextField
              fullWidth
              label={t('ban.durationDaysLabel', 'Durée (jours)')}
              type="number"
              value={banForm.durationDays}
              onChange={(e) => setBanForm(prev => ({ ...prev, durationDays: e.target.value }))}
              inputProps={{ min: 1, max: 365 }}
              sx={{ mb: 2 }}
            />
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
          <FormLabel>{t('ban.targetLabel', 'Cible')}</FormLabel>
          <RadioGroup
          row
          value={banForm.target}
          onChange={async (e) => {
          const nextTarget = e.target.value;
          const { ownerId, renterId } = getDisputePartyIds();
          const nextUserId = nextTarget === 'owner' ? ownerId : renterId;
          setBanForm(prev => ({ ...prev, target: nextTarget, userId: nextUserId }));
          // On recharge les 2 parties pour garder l'accordéon cohérent
          fetchPartiesBanSummary({ ownerId, renterId });
          // Recharger les infos utilisateur pour la nouvelle cible
          if (nextUserId) {
            try {
              const response = await restrictionAdminService.getUserBasicInfo(nextUserId);
              setBanUserInfo(response.data || response);
            } catch (err) {
              console.error('Failed to load user info:', err);
              setBanUserInfo(null);
            }
          }
          }}
          >
          <FormControlLabel value="renter" control={<Radio />} label={t('reservation.renter', 'Locataire')} />
          <FormControlLabel value="owner" control={<Radio />} label={t('reservation.owner', 'Propriétaire')} />
          </RadioGroup>
          </FormControl>
          
          <TextField
          fullWidth
          multiline
          minRows={3}
          label={t('labels.reason', 'Raison')}
          value={banForm.reason}
          onChange={(e) => setBanForm(prev => ({ ...prev, reason: e.target.value }))}
          placeholder={t('ban.reasonPlaceholder', 'Expliquez la raison du ban (liée au litige)')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)} disabled={banSubmitting}>{t('common.cancel', 'Annuler')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSubmitBan}
            disabled={
              banSubmitting ||
              !banForm.userId ||
              !banForm.reason.trim() ||
              (banForm.target === 'owner'
                ? (banSummary.owner?.userId === banForm.userId && banSummary.owner?.ban?.isBanned)
                : (banSummary.renter?.userId === banForm.userId && banSummary.renter?.ban?.isBanned))
            }
          >
            {banSubmitting ? <CircularProgress size={20} /> : t('ban.apply', 'Appliquer le ban')}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        {/* Colonne gauche */}
        <Grid item xs={12} md={8}>
          
          {/* Cash Settlement Card - Affiché uniquement pour les disputes avec paiement cash en attente de règlement */}
          {dispute.status === 'pending_settlement' && user && (
            <CashSettlementCard
              disputeId={dispute.dispute_id}
              currentUserId={user.id}
              onSettlementComplete={(updatedDispute) => {
                // Rafraîchir le dispute après résolution
                fetchDispute();
              }}
              onRefresh={fetchDispute}
            />
          )}

          {/* Admin Decision Panel - Affiché pour les admins */}
          {user?.roles?.includes('admin') && (
            <Accordion 
              expanded={adminDecisionExpanded} 
              onChange={(e, isExpanded) => setAdminDecisionExpanded(isExpanded)}
              sx={{ mb: 3 }}
              disabled={adminAuthRequest && adminAuthRequest.status === 'pending_auth'}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('admin.decisionTitle', '⚖️ Décision administrative')}
                  </Typography>
                  {adminAuthRequest && adminAuthRequest.status === 'pending_auth' && (
                    <Chip
                      icon={<ScheduleIcon />}
                      label={t('admin.awaiting3ds', 'En attente de validation 3DS')}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                  {(dispute?.status === 'resolved' || dispute?.status === 'closed') && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={t('admin.disputeClosed', 'Litige clos - Aucune modification possible')}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {adminAuthRequest && adminAuthRequest.status === 'pending_auth' ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>{t('admin.awaiting3dsTitle', 'Validation 3DS en cours')}</AlertTitle>
                  {t('admin.awaiting3dsDesc', "Cette section est désactivée car une validation 3DS est en attente pour la décision précédente. Le locataire doit valider le paiement avant qu'une nouvelle décision puisse être prise.")}
                  </Alert>
                ) : (
                  <>
                    {(() => {
                      // Compute financial totals and pass to AdminDecisionPanel
                      const reservation = fullReservation || dispute.reservation || {};
                      const currency = reservation.currency || dispute.currency || 'EUR';

                      let rentalTotal = 0;
                      let commission = 0;
                      let tax = 0;
                      let depositAmount = parseFloat(reservation.deposit?.amount || 0) || 0;

                      if (Array.isArray(reservation.payments)) {
                        reservation.payments.forEach((p) => {
                          const amt = parseFloat(p.amount || 0) || 0;
                          const type = (p.payment_type || '').toLowerCase();
                          if (type === 'rental') rentalTotal += amt;
                          else if (type === 'commission') commission += amt;
                          else if (type === 'tax') tax += amt;
                          else if (type === 'deposit') depositAmount += amt;
                        });
                      }

                      const netAmount = Math.max(0, rentalTotal - commission - tax);

                      // Préparer les infos du propriétaire et du locataire
                      const ownerInfo = reservation?.listing?.owner ? {
                        name: reservation.listing.owner.name || 'Propriétaire',
                        email: reservation.listing.owner.email || 'N/A',
                        language: reservation.listing.owner.preferences?.language || reservation.listing.owner.preferences?.display?.language || 'en'
                      } : null;

                      const renterInfo = reservation?.renter ? {
                        name: reservation.renter.name || 'Locataire',
                        email: reservation.renter.email || 'N/A',
                        language: reservation.renter.preferences?.language || reservation.renter.preferences?.display?.language || 'en'
                      } : null;

                      return (
                        <AdminDecisionPanel
                          dispute={dispute}
                          onDecisionMade={async (result) => {
                            try {
                              // Attendre que le backend finisse
                              await new Promise(resolve => setTimeout(resolve, 1000));
                              
                              // Vérifier si 3DS requis
                              const authRequest = await adminPaymentAuthService.getByDisputeId(dispute.dispute_id || dispute.id);
                              const hasPendingAuth = authRequest && authRequest.status === 'pending_auth';
                              
                              if (hasPendingAuth) {
                                enqueueSnackbar(
                                  t('admin.decisionPending3ds', '⏳ Décision enregistrée - Le locataire doit valider le paiement avec authentification 3DS'),
                                  { 
                                    variant: 'info',
                                    autoHideDuration: 8000,
                                    anchorOrigin: { vertical: 'top', horizontal: 'center' }
                                  }
                                );
                              } else {
                                enqueueSnackbar(
                                  t('admin.decisionApplied', '✅ Décision administrative appliquée avec succès'),
                                  { variant: 'success' }
                                );
                              }
                            } catch (error) {
                              enqueueSnackbar(
                                t('admin.decisionRecorded', '✅ Décision administrative enregistrée'),
                                { variant: 'success' }
                              );
                            }
                            
                            // FORCER LE RECHARGEMENT DE LA PAGE
                            window.location.reload();
                          }}
                          financialSummary={{
                            rentalTotal,
                            netAmount,
                            depositAmount,
                            currency,
                            isReal: Boolean(fullReservation)
                          }}
                          ownerInfo={ownerInfo}
                          renterInfo={renterInfo}
                        />
                      );
                    })()}
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          {/* 🆕 Ban depuis le litige - Section ADMIN uniquement */}
          {user?.roles?.includes('admin') && (
          <Paper elevation={2} sx={{ p: 2, mb: 3, borderLeft: 4, borderColor: 'error.main' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('ban.bannerTitle', '🚫 Bannissement (depuis ce litige)')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ban.bannerDesc', 'Cette action applique uniquement un ban (temporaire ou permanent) et le relie au litige via source_type/source_id.')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<BlockIcon />}
                onClick={openBanDialog}
                disabled={!(getDisputePartyIds().ownerId || getDisputePartyIds().renterId)}
              >
                {t('ban.ban', 'Bannir')}
              </Button>
            </Stack>

                {/* Accordéon: ban(s) des parties (owner + renter) */}
                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t('ban.title', 'Ban des parties')}
                      </Typography>
                      {banSummary.loading && <Chip size="small" label={t('common.loading', 'Chargement…')} />}
                      {!banSummary.loading && (banSummary.owner?.ban?.isBanned || banSummary.renter?.ban?.isBanned) && (
                        <Chip size="small" color="error" label={t('ban.someActive', 'Au moins un ban actif')} />
                      )}
                      {!banSummary.loading && !(banSummary.owner?.ban?.isBanned || banSummary.renter?.ban?.isBanned) && (
                        <Chip size="small" label={t('ban.noneActive', 'Aucun ban actif')} />
                      )}
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    {banSummary.error && (
                      <Alert severity="warning" sx={{ mb: 2 }}>{banSummary.error}</Alert>
                    )}

                    {!banSummary.loading && !(banSummary.owner?.userId || banSummary.renter?.userId) && (
                      <Typography variant="body2" color="text.secondary">
                        {t('ban.cannotDetermineIds', "Impossible de déterminer les IDs owner/renter.")}
                      </Typography>
                    )}

                    {/* OWNER */}
                    {banSummary.owner?.userId && (
                      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {t('reservation.owner', 'Propriétaire')}
                          </Typography>
                          {banSummary.owner?.ban?.isBanned ? (
                            <Chip size="small" color="error" label={t('ban.banned', 'Banni')} />
                          ) : (
                            <Chip size="small" label={t('ban.notBanned', 'Non banni')} />
                          )}
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {t('labels.user', 'Utilisateur :')} <strong>{banSummary.owner.userId}</strong>
                        </Typography>
                        {banSummary.owner?.ban?.isBanned && (
                          <>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {t('labels.type', 'Type :')} <strong>{banSummary.owner.ban.type}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {t('labels.duration', 'Durée :')} <strong>{banSummary.owner.ban.type === 'permanent' ? t('ban.permanent', 'Permanent') : (banSummary.owner.ban.until ? `${t('ban.until', 'Jusqu’au')} ${format(new Date(banSummary.owner.ban.until), 'dd MMM yyyy HH:mm', { locale: dateLocale })}` : t('ban.temporary', 'Temporaire'))}</strong>
                            </Typography>
                          </>
                        )}
                        {banSummary.owner?.ban?.reason && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">{t('labels.reason', 'Raison')}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{banSummary.owner.ban.reason}</Typography>
                          </Alert>
                        )}
                        {banSummary.owner?.ban?.isBanned && user?.roles?.includes('admin') && (
                          <Box sx={{ mt: 1.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleOpenUnbanDialog(banSummary.owner.userId, 'Propriétaire')}
                            >
                              {t('ban.unban', 'Débannir')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* RENTER */}
                    {banSummary.renter?.userId && (
                      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Locataire
                          </Typography>
                          {banSummary.renter?.ban?.isBanned ? (
                            <Chip size="small" color="error" label="Banni" />
                          ) : (
                            <Chip size="small" label="Non banni" />
                          )}
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {t('labels.user', 'Utilisateur :')} <strong>{banSummary.renter.userId}</strong>
                        </Typography>
                        {banSummary.renter?.ban?.isBanned && (
                          <>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {t('labels.type', 'Type :')} <strong>{banSummary.renter.ban.type}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {t('labels.duration', 'Durée :')} <strong>{banSummary.renter.ban.type === 'permanent' ? t('ban.permanent', 'Permanent') : (banSummary.renter.ban.until ? `${t('ban.until', 'Jusqu’au')} ${format(new Date(banSummary.renter.ban.until), 'dd MMM yyyy HH:mm', { locale: dateLocale })}` : t('ban.temporary', 'Temporaire'))}</strong>
                            </Typography>
                          </>
                        )}
                        {banSummary.renter?.ban?.reason && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">{t('labels.reason', 'Raison')}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{banSummary.renter.ban.reason}</Typography>
                          </Alert>
                        )}
                        {banSummary.renter?.ban?.isBanned && user?.roles?.includes('admin') && (
                          <Box sx={{ mt: 1.5 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleOpenUnbanDialog(banSummary.renter.userId, 'Locataire')}
                            >
                              {t('ban.unban', 'Débannir')}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                    {user?.roles?.includes('admin') && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const { ownerId, renterId } = getDisputePartyIds();
                            fetchPartiesBanSummary({ ownerId, renterId });
                          }}
                        >
                          {t('common.refresh', 'Rafraîchir')}
                        </Button>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                {!(getDisputePartyIds().ownerId || getDisputePartyIds().renterId) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('ban.cannotDetermineParties', 'Impossible de déterminer les parties du litige (owner/renter) depuis la réservation.')}
                  </Alert>
                )}
          </Paper>
          )}

          {/* 🆕 Statut de bannissement - Section OWNER/RENTER uniquement */}
          {!user?.roles?.includes('admin') && (
          <Accordion elevation={2} sx={{ mb: 3, borderLeft: 4, borderColor: 'warning.main' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                <WarningIcon color="warning" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('ban.statusTitle', 'Statut de bannissement')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('ban.statusDesc', 'Information sur les éventuels bannissements appliqués dans le cadre de ce litige')}
                  </Typography>
                </Box>
                <Box sx={{ ml: 'auto' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchPartiesBanStatusForParties(disputeId);
                    }}
                    disabled={partiesBanStatus.loading}
                  >
                    {t('common.refresh', 'Rafraîchir')}
                  </Button>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {partiesBanStatus.loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              )}

              {partiesBanStatus.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {partiesBanStatus.error}
                </Alert>
              )}

              {!partiesBanStatus.loading && !partiesBanStatus.error && (
                <>
                  {/* Affichage si au moins un ban actif */}
                  {(partiesBanStatus.owner?.ban?.isBanned || partiesBanStatus.renter?.ban?.isBanned) ? (
                    <Stack spacing={2}>
                      {/* OWNER */}
                      {partiesBanStatus.owner?.userId && (
                        <Box sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: partiesBanStatus.owner?.ban?.isBanned ? 'error.main' : 'divider',
                          borderWidth: partiesBanStatus.owner?.ban?.isBanned ? 2 : 1,
                          borderRadius: 1,
                          bgcolor: partiesBanStatus.owner?.ban?.isBanned ? 'error.lighter' : 'background.paper',
                          boxShadow: partiesBanStatus.owner?.ban?.isBanned ? '0 24px 60px rgba(211,47,47,0.32)' : undefined
                        }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
                            <Avatar 
                              src={getProfilePictureUrl(
                                partiesBanStatus.owner.userId === dispute?.reporter?.id
                                  ? dispute?.reporter
                                  : partiesBanStatus.owner.userId === dispute?.reportedUser?.id
                                    ? dispute?.reportedUser
                                    : dispute?.reservation?.listing?.owner
                              )}
                              sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}
                            >
                              {!getProfilePictureUrl(
                                partiesBanStatus.owner.userId === dispute?.reporter?.id
                                  ? dispute?.reporter
                                  : partiesBanStatus.owner.userId === dispute?.reportedUser?.id
                                    ? dispute?.reportedUser
                                    : dispute?.reservation?.listing?.owner
                              ) && <PersonIcon fontSize="small" />}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                {partiesBanStatus.owner.userId === dispute?.reporter?.id
                                  ? t('labels.reporter', 'Déclarant')
                                  : partiesBanStatus.owner.userId === dispute?.reportedUser?.id
                                    ? t('labels.accused', 'Mise en cause')
                                    : t('reservation.owner', 'Propriétaire')}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {partiesBanStatus.owner.userId === dispute?.reporter?.id
                                  ? (dispute?.reporter?.name || dispute?.reporter?.email || t('detail.unknownUser', 'Utilisateur inconnu'))
                                  : partiesBanStatus.owner.userId === dispute?.reportedUser?.id
                                    ? (dispute?.reportedUser?.name || dispute?.reportedUser?.email || t('detail.unknownUser', 'Utilisateur inconnu'))
                                    : (dispute?.reservation?.listing?.owner?.name || dispute?.reservation?.listing?.owner?.email || t('detail.unknownUser', 'Utilisateur inconnu'))}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                ({t('reservation.owner', 'Propriétaire')})
                              </Typography>
                              {partiesBanStatus.owner?.ban?.isBanned ? (
                                <Chip size="small" color="error" label={t('ban.banned', 'Banni')} />
                              ) : (
                                <Chip size="small" color="success" label={t('ban.notBanned', 'Non banni')} />
                              )}
                            </Box>
                          </Stack>
                          
                          {partiesBanStatus.owner?.ban?.isBanned && (
                            <>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {t('labels.type', 'Type :')} <strong>{partiesBanStatus.owner.ban.type === 'permanent' ? t('ban.permanent', 'Permanent') : t('ban.temporary', 'Temporaire')}</strong>
                              </Typography>
                              {partiesBanStatus.owner.ban.until && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {t('ban.until', "Jusqu’au")} : <strong>{format(new Date(partiesBanStatus.owner.ban.until), 'dd/MM/yyyy', { locale: dateLocale })}</strong>
                                </Typography>
                              )}
                              {partiesBanStatus.owner.ban.reason && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  <Typography variant="subtitle2">{t('labels.reason', 'Raison')}</Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{partiesBanStatus.owner.ban.reason}</Typography>
                                </Alert>
                              )}
                            </>
                          )}
                        </Box>
                      )}

                      {/* RENTER */}
                      {partiesBanStatus.renter?.userId && (
                        <Box sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: partiesBanStatus.renter?.ban?.isBanned ? 'error.main' : 'divider',
                          borderWidth: partiesBanStatus.renter?.ban?.isBanned ? 2 : 1,
                          borderRadius: 1,
                          bgcolor: partiesBanStatus.renter?.ban?.isBanned ? 'error.lighter' : 'background.paper',
                          boxShadow: partiesBanStatus.renter?.ban?.isBanned ? '0 24px 60px rgba(211,47,47,0.32)' : undefined
                        }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
                            <Avatar 
                              src={getProfilePictureUrl(
                                partiesBanStatus.renter.userId === dispute?.reporter?.id
                                  ? dispute?.reporter
                                  : partiesBanStatus.renter.userId === dispute?.reportedUser?.id
                                    ? dispute?.reportedUser
                                    : dispute?.reservation?.renter
                              )}
                              sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}
                            >
                              {!getProfilePictureUrl(
                                partiesBanStatus.renter.userId === dispute?.reporter?.id
                                  ? dispute?.reporter
                                  : partiesBanStatus.renter.userId === dispute?.reportedUser?.id
                                    ? dispute?.reportedUser
                                    : dispute?.reservation?.renter
                              ) && <PersonIcon fontSize="small" />}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                {partiesBanStatus.renter.userId === dispute?.reporter?.id
                                  ? t('labels.reporter', 'Déclarant')
                                  : partiesBanStatus.renter.userId === dispute?.reportedUser?.id
                                    ? t('labels.accused', 'Mise en cause')
                                    : t('reservation.renter', 'Locataire')}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {partiesBanStatus.renter.userId === dispute?.reporter?.id
                                  ? (dispute?.reporter?.name || dispute?.reporter?.email || t('detail.unknownUser', 'Utilisateur inconnu'))
                                  : partiesBanStatus.renter.userId === dispute?.reportedUser?.id
                                    ? (dispute?.reportedUser?.name || dispute?.reportedUser?.email || t('detail.unknownUser', 'Utilisateur inconnu'))
                                    : (dispute?.reservation?.renter?.name || dispute?.reservation?.renter?.email || t('detail.unknownUser', 'Utilisateur inconnu'))}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                ({t('reservation.renter', 'Locataire')})
                              </Typography>
                              {partiesBanStatus.renter?.ban?.isBanned ? (
                                <Chip size="small" color="error" label={t('ban.banned', 'Banni')} />
                              ) : (
                                <Chip size="small" color="success" label={t('ban.notBanned', 'Non banni')} />
                              )}
                            </Box>
                          </Stack>
                          
                          {partiesBanStatus.renter?.ban?.isBanned && (
                            <>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {t('labels.type', 'Type :')} <strong>{partiesBanStatus.renter.ban.type === 'permanent' ? t('ban.permanent', 'Permanent') : t('ban.temporary', 'Temporaire')}</strong>
                              </Typography>
                              {partiesBanStatus.renter.ban.until && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {t('ban.until', "Jusqu’au")} : <strong>{format(new Date(partiesBanStatus.renter.ban.until), 'dd/MM/yyyy', { locale: dateLocale })}</strong>
                                </Typography>
                              )}
                              {partiesBanStatus.renter.ban.reason && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                  <Typography variant="subtitle2">{t('labels.reason', 'Raison')}</Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{partiesBanStatus.renter.ban.reason}</Typography>
                                </Alert>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="success">
                      {t('ban.noneActiveLong', 'Aucun bannissement actif pour les parties de ce litige.')}
                    </Alert>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
          )}

          {/* 🆕 Récapitulatifs des pénalités appliquées - Visible pour tous en lecture seule */}
          {appliedPenalties.length > 0 && (
            <Accordion 
              expanded={penaltySummaryExpanded} 
              onChange={(e, isExpanded) => setPenaltySummaryExpanded(isExpanded)}
              sx={{ mb: 3 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('penalties.summaryTitle', "📋 Récapitulatif d'Application de Pénalité")} ({appliedPenalties.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {appliedPenalties.slice(0, visiblePenaltiesCount).map((penalty, index) => (
                    <Accordion key={penalty.id} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight={600}>
                            Pénalité #{penalty.id}
                          </Typography>
                          <Chip 
                            label={`${penalty.amount} ${penalty.currency}`} 
                            color="error" 
                            size="small" 
                          />
                          <Chip 
                            label={getPenaltyStatusInfo(penalty.status, t).label} 
                            size="small" 
                            variant="outlined"
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <PenaltyExecutionSummary 
                          penalty={penalty}
                          dispute={dispute}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  
                  {/* Bouton Charger plus */}
                  {visiblePenaltiesCount < appliedPenalties.length && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setVisiblePenaltiesCount(prev => prev + PENALTIES_PER_PAGE)}
                        sx={{ minWidth: 200 }}
                      >
                        {t('common.loadMore', 'Charger plus')} ({appliedPenalties.length - visiblePenaltiesCount} restant{appliedPenalties.length - visiblePenaltiesCount > 1 ? 's' : ''})
                      </Button>
                    </Box>
                  )}
                  
                  {/* Message quand toutes les pénalités sont affichées */}
                  {visiblePenaltiesCount >= appliedPenalties.length && appliedPenalties.length > PENALTIES_PER_PAGE && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('penalties.allDisplayed', '✓ Toutes les pénalités sont affichées')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Monetary Penalty Form - Affiché pour les admins */}
          {user?.roles?.includes('admin') && (
            <>
              {/* 🆕 Analyse Financière - Affichée AVANT le formulaire de pénalité */}
              <Accordion 
                expanded={financialAnalysisExpanded} 
                onChange={(e, isExpanded) => setFinancialAnalysisExpanded(isExpanded)}
                sx={{ mb: 3 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('admin.financialAnalysisTitle', '💰 Analyse Financière - Options de Recouvrement')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DisputeFinancialAnalysis disputeId={dispute.dispute_id || dispute.id} />
                </AccordionDetails>
              </Accordion>

              {/* Formulaire de pénalité */}
              <Accordion 
                expanded={penaltyFormExpanded} 
                onChange={(e, isExpanded) => setPenaltyFormExpanded(isExpanded)}
                sx={{ mb: 3 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('penalties.applyMonetary', '💸 Appliquer une sanction monétaire')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MonetaryPenaltyForm
                    sourceType="dispute"
                    sourceId={dispute.dispute_id || dispute.id}
                    parties={{
                      owner: dispute.reservation?.listing?.owner ? {
                        id: dispute.reservation.listing.ownerId || dispute.reservation.listing.owner.id,
                        name: `${dispute.reservation.listing.owner.firstName || ''} ${dispute.reservation.listing.owner.lastName || ''}`.trim() || 'Propriétaire',
                        hasStripe: Boolean(dispute.reservation.listing.owner.stripeAccountId)
                      } : null,
                      renter: dispute.reservation?.renter ? {
                        id: dispute.reservation.renter_id || dispute.reservation.renter.id,
                        name: `${dispute.reservation.renter.firstName || ''} ${dispute.reservation.renter.lastName || ''}`.trim() || 'Locataire',
                        hasStripe: Boolean(dispute.reservation.renter.stripeAccountId)
                      } : null
                    }}
                    reservationId={dispute.reservation_id}
                    reservation={dispute.reservation}
                    defaultCurrency={dispute.reservation?.currency || 'EUR'}
                    suggestedAmount={dispute.requested_amount || ''}
                    suggestedReason={t('penalties.disputeReason', 'Suite au litige #{{id}}', { id: (dispute.dispute_id || dispute.id)?.substring(0, 8) })}
                    onPenaltyCreated={(penalty) => {
                      console.log('Penalty created:', penalty);
                    }}
                    onPenaltyApplied={(penalty) => {
                      console.log('Penalty applied:', penalty);
                      fetchDispute();
                    }}
                    title=""
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* Private Channels Panel - Affiché pour les litiges escaladés */}
          {dispute.status === 'escalated' && user && (
            <PrivateChannelsPanel
              dispute={dispute}
              currentUser={user}
              onMessageSent={() => {
                // Optionnel: rafraîchir les événements
              }}
            />
          )}
          
          {/* Amicable Resolution Section */}
          {/* Condition élargie: l'utilisateur est soit créateur, soit against_user, soit renter, soit owner de la réservation */}
          {/* Masqué si le litige est résolu, fermé, en attente de règlement cash, ou escaladé à l'admin */}
          {user && (
            user.id === dispute.created_by || 
            user.id === dispute.against_user_id ||
            user.id === dispute.reservation?.renter_id ||
            user.id === dispute.reservation?.listing?.ownerId
          ) && !['resolved', 'closed', 'pending_settlement', 'escalated'].includes(dispute.status) && (
            <Paper elevation={2} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'info.main', color: 'text.primary', borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1}>
                  <HandshakeIcon />
                  <Typography variant="h6">{t('amicable.title', 'Résolution Amiable')}</Typography>
                </Box>
                {!(dispute.metadata?.amicable_proposal?.status === 'pending') && 
                 !(dispute.metadata?.amicable_proposal?.status === 'accepted' && 
                   dispute.status !== 'resolved' && 
                   dispute.status !== 'pending_settlement') && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setCompensationType(defaultCompensationType);
                      setProposalDialogOpen(true);
                    }}
                  >
                    {t('amicable.propose', 'Proposer une solution')}
                  </Button>
                )}
              </Stack>
              
              {dispute.metadata?.amicable_proposal?.status === 'pending' && (
                <Card sx={{ mt: 2, bgcolor: user.id === dispute.metadata.amicable_proposal.proposed_by ? 'grey.100' : 'success.50', boxShadow: (theme) => `0 8px 20px rgba(33,33,33,0.45)` }}>
                  <CardContent>
                    {user.id === dispute.metadata.amicable_proposal.proposed_by ? (
                      <>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          {t('amicable.proposalSent', '📤 Votre proposition a été envoyée')}
                        </Typography>
                        {/* 🔄 Afficher le type de compensation */}
                        <Chip 
                          label={dispute.metadata.amicable_proposal.compensation_type === 'renter_refund' 
                            ? `👤 ${t('amicable.renterRefund', 'Remboursement au locataire')}` 
                            : dispute.metadata.amicable_proposal.compensation_type === 'owner_compensation'
                              ? `🏠 ${t('amicable.ownerCompensation', 'Dédommagement au propriétaire')}`
                              : `🛑 ${t('amicable.cancellation', 'Annulation de la réservation')}`}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                        {dispute.metadata.amicable_proposal.compensation_type !== 'reservation_cancellation' && (
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {t('amicable.amountProposed', 'Montant proposé :')} <strong>{dispute.metadata.amicable_proposal.amount} {getCurrencySymbol(dispute.metadata.amicable_proposal.currency || dispute?.reservation?.currency || 'EUR')}</strong>
                          </Typography>
                        )}
                        {dispute.metadata.amicable_proposal.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            💬 "{dispute.metadata.amicable_proposal.message}"
                          </Typography>
                        )}
                        <Typography variant="caption" display="block" mt={2} color="text.secondary">
                          ⏳ {t('amicable.waitingForResponse', 'En attente de la réponse de l\'autre partie...')}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle1" fontWeight="bold" color="success.dark">
                          {t('amicable.proposalReceived', '📬 Nouvelle proposition reçue')}
                        </Typography>
                        {/* 🔄 Afficher le type de compensation */}
                        <Chip 
                          label={dispute.metadata.amicable_proposal.compensation_type === 'renter_refund' 
                            ? `👤 ${t('amicable.renterRefund', 'Remboursement au locataire')}` 
                            : dispute.metadata.amicable_proposal.compensation_type === 'owner_compensation'
                              ? `🏠 ${t('amicable.ownerCompensation', 'Dédommagement au propriétaire')}`
                              : `🛑 ${t('amicable.cancellation', 'Annulation de la réservation')}`}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                        {dispute.metadata.amicable_proposal.compensation_type !== 'reservation_cancellation' && (
                          <Typography variant="body1" sx={{ mt: 1 }}>
                            {t('amicable.amountProposed', 'Montant proposé :')} <strong>{dispute.metadata.amicable_proposal.amount} {getCurrencySymbol(dispute.metadata.amicable_proposal.currency || dispute?.reservation?.currency || 'EUR')}</strong>
                          </Typography>
                        )}
                        {dispute.metadata.amicable_proposal.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            💬 "{dispute.metadata.amicable_proposal.message}"
                          </Typography>
                        )}
                        <Box mt={2}>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button 
                              variant="contained" 
                              color="success" 
                              onClick={() => setAcceptConfirmDialogOpen(true)}
                              startIcon={<CheckCircleIcon />}
                              disabled={sending}
                            >
                              {t('amicable.accept', 'Accepter')}
                            </Button>
                            <Button 
                              variant="outlined" 
                              color="error" 
                              onClick={() => setRejectDialogOpen(true)}
                              startIcon={<CloseIcon />}
                              disabled={sending}
                            >
                              {t('amicable.reject', 'Refuser')}
                            </Button>
                            <Button 
                              variant="outlined" 
                              color="primary" 
                              onClick={() => {
                                // Initialiser avec le type de la proposition actuelle (ou par défaut)
                                const currentType = dispute?.metadata?.amicable_proposal?.compensation_type || 'owner_compensation';
                                setCounterCompensationType(sanitizeCompensationType(currentType));
                                setCounterProposalDialogOpen(true);
                              }}
                              startIcon={<HandshakeIcon />}
                              disabled={sending}
                            >
                              {t('amicable.counter', 'Contre-proposer')}
                            </Button>
                          </Stack>
                          <Typography variant="caption" display="block" mt={2} color="text.secondary">
                            ℹ️ {dispute.metadata.amicable_proposal.compensation_type === 'reservation_cancellation'
                              ? t('amicable.acceptDisclaimerCancellation', 'En acceptant, la réservation sera annulée sans transaction financière et le litige sera clos.')
                              : t('amicable.acceptDisclaimer', 'En acceptant, le montant sera immédiatement prélevé sur la caution et le litige sera clos.')}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Afficher si proposition refusée */}
              {dispute.metadata?.amicable_proposal?.status === 'rejected' && (
                <Card sx={{ mt: 2, border: '1px solid', borderColor: 'error.main', borderRadius: 1, boxShadow: (theme) => `0 8px 20px rgba(255,0,0,0.18)` }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
                      ❌ {t('amicable.proposalRejected', 'La dernière proposition a été refusée')}
                    </Typography>
                    {dispute.metadata.amicable_proposal.compensation_type === 'reservation_cancellation' ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('amicable.rejectedCancellation', 'Proposition d\'annulation refusée')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('amicable.rejectedAmount', 'Montant refusé :')} <strong>{dispute.metadata.amicable_proposal.amount} {getCurrencySymbol(dispute.metadata.amicable_proposal.currency || dispute?.reservation?.currency || 'EUR')}</strong>
                      </Typography>
                    )}
                    {dispute.metadata.amicable_proposal.rejection_reason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        💬 Raison: "{dispute.metadata.amicable_proposal.rejection_reason}"
                      </Typography>
                    )}
                    <Box mt={2}>
                      <Button 
                        variant="contained" 
                        color="info" 
                        onClick={() => {
                          setCompensationType(defaultCompensationType);
                          setProposalDialogOpen(true);
                        }}
                        startIcon={<HandshakeIcon />}
                        sx={{ color: 'common.white' }}
                      >
                        {t('amicable.proposeNew', 'Faire une nouvelle proposition')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Carte spéciale: Proposition acceptée en attente de paiement 3DS */}
              {dispute.metadata?.amicable_proposal?.status === 'accepted' && 
               dispute.status !== 'resolved' && 
               dispute.status !== 'pending_settlement' && (
                <Card sx={{
                  mt: 2,
                  bgcolor: 'transparent',
                  border: '1px solid',
                  borderColor: 'info.main',
                  boxShadow: (theme) => `0 10px 30px ${alpha(theme.palette.info.main, 0.72)}`
                }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
                      ⏳ {t('amicable.proposalAcceptedPending', 'Proposition acceptée - En attente de paiement')}
                    </Typography>
                    {dispute.metadata.amicable_proposal.compensation_type === 'reservation_cancellation' ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('amicable.acceptedCancellation', 'Annulation de la réservation acceptée')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('amicable.acceptedAmount', 'Montant accepté :')} <strong>{dispute.metadata.amicable_proposal.amount} {getCurrencySymbol(dispute.metadata.amicable_proposal.currency || dispute?.reservation?.currency || 'EUR')}</strong>
                      </Typography>
                    )}
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {t('amicable.proposalAcceptedInfoMain', '💳 Cette proposition a été acceptée par les deux parties.')}
                        {dispute.metadata?.pending_dispute_authentication || dispute.metadata?.pending_auth_payment_intent_id ? (
                          <>{t('amicable.authRequired', 'Une authentification bancaire est requise pour finaliser le paiement. Veuillez cliquer sur "Retenter l\'authentification" pour compléter la transaction.')}</>
                        ) : (
                          <>{t('amicable.processing', "Le traitement du paiement est en cours. Vous recevrez une notification dès que le paiement sera confirmé ou si l'authentification bancaire est abandonnée. Vous pouvez également rafraîchir cette page dans quelques minutes pour voir le statut actualisé.")}</>
                        )}
                      </Typography>
                    </Alert>
                    {(dispute.metadata?.pending_dispute_authentication || dispute.metadata?.pending_auth_payment_intent_id) && (
                      <Box mt={2}>
                        <Button 
                          variant="contained" 
                          color="primary"
                          onClick={() => {
                            // Récupérer les détails d'auth depuis metadata
                            const pendingAuth = dispute.metadata?.pending_dispute_authentication;
                            if (pendingAuth) {
                              setAuthClientSecret(pendingAuth.client_secret);
                              setAuthPaymentMethodId(pendingAuth.payment_method_id);
                              setAuthPaymentIntentId(pendingAuth.payment_intent_id);
                              setAuthModalOpen(true);
                            }
                          }}
                          startIcon={<LockIcon />}
                          disabled={!dispute.metadata?.pending_dispute_authentication}
                        >
                          {t('amicable.retryAuthentication', 'Retenter l\'authentification')}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Bouton d'escalade - visible uniquement si au moins une proposition a été émise ET aucune n'est en attente */}
              {dispute.metadata?.proposals && dispute.metadata.proposals.length > 0 && 
               dispute.metadata?.amicable_proposal?.status !== 'pending' &&
               !(dispute.metadata?.amicable_proposal?.status === 'accepted' && 
                 dispute.status !== 'resolved' && 
                 dispute.status !== 'pending_settlement') && (
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                    {t('escalate.noAgreement', 'Vous ne parvenez pas à trouver un accord ?')}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    color="warning" 
                    onClick={() => { setEscalateDialogOpen(true); 
                                      setEscalateError(null); 
                                    }}
                    startIcon={<WarningIcon />}
                    disabled={sending}
                  >
                    {t('escalate.button', 'Escalader à un administrateur')}
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {/* 📜 Historique des propositions et contre-propositions */}
          {dispute?.metadata?.proposals && dispute.metadata.proposals.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon color="primary" />
                  {t('proposals.historyTitle', 'Historique des négociations')} 
                  <Chip 
                    label={dispute.metadata.proposals.length} 
                    size="small" 
                    color="primary"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {/* Trier les propositions par date (plus récentes en premier) */}
                  {[...dispute.metadata.proposals]
                    .sort((a, b) => new Date(b.proposed_at) - new Date(a.proposed_at))
                    .map((proposal, index) => {
                      // Déterminer le statut visuel
                      const statusConfig = {
                        pending: { color: 'warning', icon: '⏳', label: t('proposals.pending', 'En attente') },
                        accepted: { color: 'success', icon: '✅', label: t('proposals.accepted', 'Acceptée') },
                        rejected: { color: 'error', icon: '❌', label: t('proposals.rejected', 'Refusée') },
                        countered: { color: 'info', icon: '🔄', label: t('proposals.countered', 'Contre-proposée') }
                      };
                      const status = statusConfig[proposal.status] || statusConfig.pending;
                      
                      // Type de compensation
                      const compensationLabel = proposal.compensation_type === 'renter_refund' 
                        ? { icon: '👤', text: t('proposals.renterRefund', 'Remboursement locataire') }
                        : proposal.compensation_type === 'owner_compensation'
                          ? { icon: '🏠', text: t('proposals.ownerCompensation', 'Dédommagement propriétaire') }
                          : { icon: '🛑', text: t('proposals.cancellation', 'Annulation de la réservation') };

                      return (
                        <Card 
                          key={proposal.id || index} 
                          variant="outlined"
                          sx={{ 
                            borderLeft: 4, 
                            borderLeftColor: `${status.color}.main`,
                            bgcolor: proposal.is_counter_proposal ? 'action.hover' : 'background.paper'
                          }}
                        >
                          <CardContent sx={{ py: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                              {/* Info principale */}
                              <Box>
                                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                                  {proposal.is_counter_proposal && (
                                    <Chip 
                                      icon={<SwapHorizIcon />} 
                                      label={t('proposals.counterProposal', 'Contre-proposition')} 
                                      size="small" 
                                      variant="outlined"
                                      color="primary"
                                    />
                                  )}
                                  {proposal.compensation_type === 'reservation_cancellation' ? (
                                    <Typography variant="body1" fontWeight="bold">
                                      {t('proposals.cancellationNoAmount', 'Annulation de la réservation')}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body1" fontWeight="bold">
                                      {proposal.amount} {getCurrencySymbol(proposal.currency)}
                                    </Typography>
                                  )}
                                  <Chip 
                                    label={`${compensationLabel.icon} ${compensationLabel.text}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Stack>
                                
                                {proposal.message && (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                    💬 "{proposal.message}"
                                  </Typography>
                                )}
                              </Box>
                              
                              {/* Statut et date */}
                              <Box textAlign="right">
                                <Chip 
                                  label={`${status.icon} ${status.label}`}
                                  size="small"
                                  color={status.color}
                                  sx={{ mb: 0.5 }}
                                />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {proposal.proposed_at && format(new Date(proposal.proposed_at), 'dd/MM/yyyy HH:mm')}
                                </Typography>
                              </Box>
                            </Stack>

                            {/* Indicateur de changement de type si c'est une contre-proposition */}
                            {proposal.type_changed && (
                              <Alert severity="info" sx={{ mt: 1, py: 0 }} icon={<SwapHorizIcon fontSize="small" />}>
                                <Typography variant="caption">
                                  {t('proposals.typeChanged', 'Le type de compensation a été modifié dans cette contre-proposition')}
                                </Typography>
                              </Alert>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Description du litige */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon color="primary" />
                {t('detail.descriptionTitle', 'Description du litige')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                {dispute.description || t('detail.noDescription', 'Aucune description fournie')}
              </Typography>
                {dispute.context && (
                <Chip label={`${t('labels.context', 'Contexte')}: ${dispute.context}`} size="small" sx={{ mr: 1 }} />
              )}
              {dispute.escalation_level && (
                <Chip label={`${t('labels.level', 'Niveau')}: ${dispute.escalation_level}`} size="small" color="warning" />
              )}
            </AccordionDetails>
          </Accordion>

          {/* Détails de la réservation */}
          {dispute.reservation && (
            <Accordion sx={{ mt: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon color="primary" />
                  {t('detail.reservationTitle', 'Détails de la réservation')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Annonce */}
                  {dispute.reservation.listing && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <HomeIcon color="action" />
                          <Box flexGrow={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {t('reservation.listing', 'Annonce')}
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {dispute.reservation.listing.title || t('reservation.noTitle', 'Annonce sans titre')}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon />}
                            onClick={() => window.open(`/reservations/${dispute.reservation.id}`, '_blank')}
                          >
                            {t('common.view', 'Voir')}
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  {/* Dates de réservation */}
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <CalendarMonthIcon color="action" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {t('reservation.dates', 'Période de location')}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {dispute.reservation.start_date && dispute.reservation.end_date ? (
                              <>
                                {format(new Date(dispute.reservation.start_date), 'dd MMM yyyy', { locale: dateLocale })}
                                {' → '}
                                {format(new Date(dispute.reservation.end_date), 'dd MMM yyyy', { locale: dateLocale })}
                              </>
                            ) : (
                              t('reservation.noDates', 'Dates non disponibles')
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Devise */}
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <EuroIcon color="action" />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            {t('reservation.currency', 'Devise')}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {getCurrencySymbol(dispute.reservation.currency || dispute.reservation.listing?.currency || 'EUR')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Caution - Mise en évidence */}
                  {dispute.reservation.deposit && (
                    <Grid item xs={12}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2,                            
                          borderColor: 'warning.main',
                          borderWidth: 2,
                          boxShadow: (theme) => `0 12px 32px ${theme.palette.warning.main}33`
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <SecurityIcon sx={{ color: 'warning.dark', fontSize: 32 }} />
                          <Box flexGrow={1}>
                            <Typography variant="subtitle2" color="warning.dark">
                              {t('reservation.depositTitle', 'Caution (montant disponible pour compensation)')}
                            </Typography>
                            <Typography variant="h5" fontWeight="bold" color="warning.dark">
                              {dispute.reservation.deposit.amount} {getCurrencySymbol(dispute.reservation.currency || 'EUR')}                             
                            </Typography>
                          </Box>
                          <Chip 
                            label={(() => {
                              const s = dispute.reservation.deposit.status;
                              if (!s) return '';
                              if (s === 'held') return t('deposit.held', 'Bloquée');
                              if (s === 'refunded') return t('deposit.refunded', 'Remboursée');
                              if (s === 'cancelled') return t('deposit.cancelled', 'Annulée');
                              if (s === 'partially_captured') return t('deposit.partiallyCaptured', 'Partiellement capturée');
                              if (s === 'pending') return t('deposit.pending', 'En attente');
                              if (s === 'withheld') return t('deposit.withheld', 'Retenue');
                              return s;
                            })()}
                            color={dispute.reservation.deposit.status === 'held' ? 'warning' : 'default'}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Stack>
                        <Alert severity="info" sx={{ mt: 2 }}>
                          {t('reservation.depositInfo', 
                            'Ce montant est le maximum qui peut être réclamé en cas de dommages. Toute proposition de résolution amiable ne peut pas dépasser ce montant.'
                          )}
                        </Alert>
                      </Paper>
                    </Grid>
                  )}

                  {/* Parties impliquées */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('reservation.parties', 'Parties impliquées')}
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      {/* Propriétaire */}
                      {dispute.reservation.listing?.ownerId && (
                        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar 
                              src={getProfilePictureUrl(
                                dispute.reportedUser?.id === dispute.reservation.listing.ownerId 
                                  ? dispute.reportedUser
                                  : dispute.reporter?.id === dispute.reservation.listing.ownerId
                                    ? dispute.reporter
                                    : dispute.reservation.listing.owner
                              )}
                              sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}
                            >
                              {!getProfilePictureUrl(
                                dispute.reportedUser?.id === dispute.reservation.listing.ownerId 
                                  ? dispute.reportedUser
                                  : dispute.reporter?.id === dispute.reservation.listing.ownerId
                                    ? dispute.reporter
                                    : dispute.reservation.listing.owner
                              ) && <PersonIcon fontSize="small" />}
                            </Avatar>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('reservation.owner', 'Propriétaire')}
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {dispute.reportedUser?.id === dispute.reservation.listing.ownerId 
                                  ? (dispute.reportedUser?.name || dispute.reportedUser?.email || 'N/A')
                                  : (dispute.reporter?.name || dispute.reporter?.email || 'N/A')
                                }
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      )}
                      {/* Locataire */}
                      {dispute.reservation.renter && (
                        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar 
                              src={getProfilePictureUrl(
                                dispute.reportedUser?.id === dispute.reservation.renter_id || dispute.reportedUser?.id === dispute.reservation.renter?.id
                                  ? dispute.reportedUser
                                  : dispute.reporter?.id === dispute.reservation.renter_id || dispute.reporter?.id === dispute.reservation.renter?.id
                                    ? dispute.reporter
                                    : dispute.reservation.renter
                              )}
                              sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}
                            >
                              {!getProfilePictureUrl(
                                dispute.reportedUser?.id === dispute.reservation.renter_id || dispute.reportedUser?.id === dispute.reservation.renter?.id
                                  ? dispute.reportedUser
                                  : dispute.reporter?.id === dispute.reservation.renter_id || dispute.reporter?.id === dispute.reservation.renter?.id
                                    ? dispute.reporter
                                    : dispute.reservation.renter
                              ) && <PersonIcon fontSize="small" />}
                            </Avatar>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('reservation.renter', 'Locataire')}
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {dispute.reservation.renter?.name || dispute.reservation.renter?.email || 'N/A'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Section Messages - Chat Box */}
          <Paper elevation={2} sx={{ mt: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
              <SendIcon />
              <Typography variant="h6">
                {t('detail.messagesTitle', 'Messages')} 
                {dispute.events?.filter(e => 
                  (e.event_type === 'message' || e.event_type === 'comment' || e.event_type === 'proposal_made' || e.event_type === 'proposal_accepted' || e.event_type === 'proposal_rejected' || e.event_type === 'counter_proposal' || e.event_type === 'system_note') &&
                  (!e.visibility || e.visibility === 'public')
                ).length > 0 && (
                  <Chip 
                    label={dispute.events.filter(e => 
                      (e.event_type === 'message' || e.event_type === 'comment' || e.event_type === 'proposal_made' || e.event_type === 'proposal_accepted' || e.event_type === 'proposal_rejected' || e.event_type === 'counter_proposal' || e.event_type === 'system_note') &&
                      (!e.visibility || e.visibility === 'public')
                    ).length} 
                    size="small" 
                    sx={{ ml: 1, bgcolor: 'white', color: 'primary.main', fontWeight: 'bold' }} 
                  />
                )}
              </Typography>
            </Box>
            
            {/* Messages List - Exclure les messages privés */}
            <Box sx={{ 
              maxHeight: 400, 
              overflowY: 'auto', 
              p: 2, 
              bgcolor: 'grey.50',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}>
              {dispute.events?.filter(e => 
                (e.event_type === 'message' || 
                e.event_type === 'comment' || 
                e.event_type === 'proposal_made' || 
                e.event_type === 'proposal_accepted' ||
                e.event_type === 'proposal_rejected' ||
                e.event_type === 'counter_proposal' ||
                e.event_type === 'system_note') &&
                (!e.visibility || e.visibility === 'public')
              ).length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  <SendIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography variant="body2">
                    {t('detail.noMessages', 'Aucun message pour le moment. Envoyez le premier message !')}
                  </Typography>
                </Box>
              ) : (
                dispute.events
                  .filter(e => 
                    (e.event_type === 'message' || 
                    e.event_type === 'comment' || 
                    e.event_type === 'proposal_made' || 
                    e.event_type === 'proposal_accepted' ||
                    e.event_type === 'proposal_rejected' ||
                    e.event_type === 'counter_proposal' ||
                    e.event_type === 'system_note') &&
                    (!e.visibility || e.visibility === 'public')
                  )
                  .map((msg, idx) => {
                    const isCurrentUser = msg.actor?.id === user?.id;
                    const isProposal = msg.event_type === 'proposal_made' || msg.event_type === 'proposal_accepted' || msg.event_type === 'counter_proposal';
                    const isCounterProposal = msg.event_type === 'counter_proposal';
                    const isRejection = msg.event_type === 'proposal_rejected';
                    const isSystemNote = msg.event_type === 'system_note';
                    
                    // Affichage spécial pour les notes système (centrées)
                    if (isSystemNote) {
                      return (
                        <Box 
                          key={idx} 
                          sx={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            width: '100%',
                            my: 1
                          }}
                        >
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 1.5, 
                              px: 3,
                              bgcolor: 'grey.200',
                              borderRadius: 4,
                              maxWidth: '85%',
                              textAlign: 'center'
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {msg.message}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                              {new Date(msg.created_at).toLocaleString('fr-FR', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric',
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })}
                            </Typography>
                          </Paper>
                        </Box>
                      );
                    }
                    
                    return (
                      <Box 
                        key={idx} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: isCurrentUser ? 'flex-end' : 'flex-start',
                          width: '100%'
                        }}
                      >
                        <Box sx={{ 
                          maxWidth: '75%',
                          display: 'flex',
                          flexDirection: isCurrentUser ? 'row-reverse' : 'row',
                          alignItems: 'flex-end',
                          gap: 1
                        }}>
                          {!isCurrentUser && (
                            <Avatar 
                              src={getProfilePictureUrl(msg.actor)} 
                              sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                            >
                              {!getProfilePictureUrl(msg.actor) && <PersonIcon fontSize="small" />}
                            </Avatar>
                          )}
                          <Box>
                            {!isCurrentUser && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                {msg.actor?.name || msg.actor?.email || t('detail.unknownUser', 'Utilisateur')}
                              </Typography>
                            )}
                            <Paper 
                              elevation={1}
                              sx={{ 
                                p: 2, 
                                bgcolor: isRejection
                                  ? 'error.light'
                                  : isCounterProposal
                                    ? 'info.light'
                                    : isProposal 
                                      ? 'success.light' 
                                      : isCurrentUser 
                                        ? 'primary.main' 
                                        : 'white',
                                color: isRejection
                                  ? 'error.contrastText'
                                  : isCounterProposal
                                    ? 'info.contrastText'
                                    : isProposal 
                                      ? 'success.contrastText'
                                      : isCurrentUser 
                                        ? 'white' 
                                        : 'text.primary',
                                borderRadius: 2,
                                borderTopLeftRadius: !isCurrentUser ? 0 : 16,
                                borderTopRightRadius: isCurrentUser ? 0 : 16,
                              }}
                            >
                              {isRejection && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <CloseIcon fontSize="small" />
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {t('eventTypes.proposalRejected', 'Proposition refusée')}
                                  </Typography>
                                  {Number(msg.metadata?.amount) > 0 && (
                                    <Chip 
                                      label={`${msg.metadata.amount} ${getCurrencySymbol(msg.metadata.currency || dispute?.reservation?.currency || 'EUR')}`}
                                      size="small"
                                      sx={{ bgcolor: 'white', color: 'error.dark', fontWeight: 'bold' }}
                                    />
                                  )}
                                </Box>
                              )}
                              {isProposal && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <HandshakeIcon fontSize="small" />
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {msg.event_type === 'counter_proposal'
                                      ? t('eventTypes.counterProposal', '🔄 Contre-proposition')
                                      : msg.event_type === 'proposal_made' 
                                        ? t('eventTypes.proposalMade', 'Proposition amiable')
                                        : t('eventTypes.proposalAccepted', 'Proposition acceptée')}
                                  </Typography>
                                  {msg.metadata?.compensation_type !== 'reservation_cancellation' && msg.metadata?.amount && parseFloat(msg.metadata.amount) > 0 && (
                                    <Chip 
                                      label={`${msg.metadata.amount} ${getCurrencySymbol(msg.metadata.currency || dispute?.reservation?.currency || 'EUR')}`}
                                      size="small"
                                      sx={{ bgcolor: 'white', color: isCounterProposal ? 'info.dark' : 'success.dark', fontWeight: 'bold' }}
                                    />
                                  )}
                                  {/* Afficher le type de compensation */}
                                  {msg.metadata?.compensation_type && (
                                    <Chip 
                                      label={
                                        msg.metadata.compensation_type === 'renter_refund'
                                          ? t('labels.renterRefundShort', '👤 Remboursement locataire')
                                          : msg.metadata.compensation_type === 'owner_compensation'
                                            ? t('labels.ownerCompensationShort', '🏠 Dédommagement propriétaire')
                                            : t('labels.cancellation', '🛑 Annulation de la réservation')
                                      }
                                      size="small"
                                      variant="outlined"
                                      sx={{ bgcolor: 'white' }}
                                    />
                                  )}
                                </Box>
                              )}
                              {msg.message && msg.event_type !== 'proposal_accepted' ? (
                                <Typography variant="body2">
                                  {msg.message}
                                </Typography>
                              ) : msg.event_type === 'proposal_accepted' ? (
                                dispute.status === 'resolved' ? (
                                  <Typography variant="body2">
                                    {t('detail.progressTitle', 'Progression du litige')} : {t('workflow.resolved', 'Résolu')}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2">
                                    {t('detail.progressTitle', 'Progression du litige')} : {t('workflow.processingAcceptedProposal', 'Le traitement de la proposition acceptée est en cours et sera affiché sous peu')}
                                  </Typography>
                                )
                              ) : (
                                // Fallback: conserver les anciens libellés pour proposition/rejet sans message
                                (isProposal && (
                                  <Typography variant="body2">
                                    {t('detail.proposalNoMessage', 'Proposition envoyée')}
                                  </Typography>
                                )) || (isRejection && (
                                  <Typography variant="body2">
                                    {t('detail.proposalRejectedNoReason', 'Proposition refusée sans commentaire')}
                                  </Typography>
                                ))
                              )}
                              {/* Display attachments */}
                              {msg.eventAttachments && msg.eventAttachments.length > 0 && (
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {msg.eventAttachments.map((att, attIdx) => {
                                    const isImage = att.file_type && att.file_type.startsWith && att.file_type.startsWith('image');
                                    const name = att.metadata?.original_name || (att.file_url && att.file_url.split('/').pop());
                                    return (
                                      <Box key={attIdx} sx={{ display: 'flex', alignItems: 'center' }}>
                                        {isImage ? (
                                          <Box
                                            sx={{
                                              cursor: 'pointer',
                                              borderRadius: 1,
                                              overflow: 'hidden',
                                              border: '2px solid',
                                              borderColor: isCurrentUser ? 'rgba(255,255,255,0.3)' : 'grey.300'
                                            }}
                                            onClick={() => window.open(getFileUrl(att.file_url), '_blank')}
                                          >
                                            <img
                                              src={getFileUrl(att.thumbnail_url || att.file_url)}
                                              alt={t('detail.attachment', 'Pièce jointe')}
                                              style={{ width: 80, height: 80, objectFit: 'cover', display: 'block' }}
                                            />
                                          </Box>
                                        ) : (
                                          <Box
                                            onClick={() => window.open(getFileUrl(att.file_url), '_blank')}
                                            sx={{
                                              cursor: 'pointer',
                                              p: 1,
                                              borderRadius: 1,
                                              bgcolor: 'grey.100',
                                              border: '1px solid',
                                              borderColor: 'grey.300',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1
                                            }}
                                          >
                                            <AttachFileIcon />
                                            <Typography variant="caption" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                              {name}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })}
                                </Box>
                              )}
                            </Paper>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block', 
                                mt: 0.5, 
                                textAlign: isCurrentUser ? 'right' : 'left',
                                px: 1
                              }}
                            >
                              {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm', { locale: dateLocale })}
                            </Typography>
                          </Box>
                          {isCurrentUser && (
                            <Avatar 
                              src={getProfilePictureUrl(user)} 
                              sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
                            >
                              {!getProfilePictureUrl(user) && <PersonIcon fontSize="small" />}
                            </Avatar>
                          )}
                        </Box>
                      </Box>
                    );
                  })
              )}
            </Box>

            {/* Input zone inside the messages box */}
            {dispute.status !== 'closed' && dispute.status !== 'resolved' && (
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'white' }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <IconButton 
                    component="label"
                    color="primary"
                    disabled={sending}
                  >
                    <AddPhotoAlternateIcon />
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="*/*"
                      ref={fileInputRef}
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          setAttachments(Array.from(e.target.files));
                        }
                      }}
                    />
                  </IconButton>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    variant="outlined"
                    placeholder={t('detail.typeMessage', 'Tapez votre message...')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <IconButton 
                    color="primary" 
                    onClick={handleSendMessage}
                    disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                    sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' }
                    }}
                  >
                    {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  </IconButton>
                </Stack>
                {attachments.length > 0 && (
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    📎 {attachments.length} {t('common.filesSelected', 'fichier(s) sélectionné(s)')}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>

          {/* Pièces jointes */}
          {dispute.attachments && dispute.attachments.length > 0 && (
            <Accordion defaultExpanded sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon color="primary" />
                  {t('detail.attachmentsTitle', 'Pièces jointes')} ({dispute.attachments.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ImageList cols={3} gap={12}>
                  {dispute.attachments.map((att, idx) => {
                    const isImage = att.file_type && att.file_type.startsWith && att.file_type.startsWith('image');
                    const name = att.metadata?.original_name || (att.file_url && att.file_url.split('/').pop());
                    return (
                      <ImageListItem key={idx} sx={{ cursor: 'pointer', borderRadius: 2, overflow: 'hidden' }}>
                        {isImage ? (
                          <img
                            src={getFileUrl(att.thumbnail_url || att.file_url)}
                            alt={t('detail.photo', 'Photo {{number}}', { number: idx + 1 })}
                            loading="lazy"
                            onClick={() => window.open(getFileUrl(att.file_url), '_blank')}
                            style={{ height: 180, objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            onClick={() => window.open(getFileUrl(att.file_url), '_blank')}
                            sx={{
                              height: 180,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexDirection: 'column',
                              p: 2,
                              bgcolor: 'grey.100'
                            }}
                          >
                            <AttachFileIcon fontSize="large" />
                            <Typography variant="caption" sx={{ mt: 1, textAlign: 'center' }}>{name}</Typography>
                          </Box>
                        )}
                      </ImageListItem>
                    );
                  })}
                </ImageList>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Historique */}
          {dispute.events && dispute.events.length > 0 && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" />
                  {t('detail.eventsTitle', 'Historique des événements')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {dispute.events.map((event, idx) => {
                    // Parse event_type si c'est un JSON string
                    let eventTypeLabel = event.event_type || 'Événement';
                    let eventDetails = null;
                    
                    if (typeof event.event_type === 'string' && event.event_type.includes('{')) {
                      try {
                        eventDetails = JSON.parse(event.event_type);
                        eventTypeLabel = eventDetails.action || eventDetails.type || 'Événement';
                      } catch (e) {
                        // Si parsing échoue, garder la valeur originale
                      }
                    }

                    // Traduction des types d'événements
                    const eventTypeTranslations = {
                      'creation': t('eventTypes.creation', 'Création du litige'),
                        'status_change': t('eventTypes.statusChange', 'Changement de statut'),
                        'comment': t('eventTypes.comment', 'Commentaire ajouté'),
                        'message': t('eventTypes.message', 'Message'),
                        'resolution': t('eventTypes.resolution', 'Résolution'),
                        'escalation': t('eventTypes.escalation', 'Escalade'),
                        'system_note': t('eventTypes.systemNote', 'Note système'),
                        'proposal_made': t('eventTypes.proposalMade', '💰 Proposition amiable envoyée'),
                        'counter_proposal': t('eventTypes.counterProposal', '🔄 Contre-proposition'),
                        'proposal_accepted': t('eventTypes.proposalAccepted', '✅ Proposition acceptée'),
                        'proposal_rejected': t('eventTypes.proposalRejected', '❌ Proposition refusée')
                    };

                    // Use mapped translation if available, otherwise try i18n key `eventTypes.<eventTypeLabel>` with fallback
                    const displayType = eventTypeTranslations[eventTypeLabel] || t(`eventTypes.${eventTypeLabel}`, eventTypeLabel);
                    
                    // Highlight proposal events
                    const isProposalEvent = eventTypeLabel === 'proposal_made' || eventTypeLabel === 'proposal_accepted';
                    const isRejectionEvent = eventTypeLabel === 'proposal_rejected';
                    const borderColor = isRejectionEvent ? 'error.main' : isProposalEvent ? 'success.main' : 'primary.main';
                    const bgColor = isRejectionEvent ? 'error.50' : isProposalEvent ? 'success.50' : 'transparent';

                    return (
                      <Card key={idx} variant="outlined" sx={{ borderLeft: 4, borderLeftColor: borderColor, bgcolor: bgColor }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold" color={isRejectionEvent ? 'error.dark' : isProposalEvent ? 'success.dark' : 'inherit'}>
                              {displayType}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(event.created_at), 'dd/MM/yyyy HH:mm', { locale: dateLocale })}
                            </Typography>
                          </Stack>
                          
                          {event.message && (
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {event.message}
                            </Typography>
                          )}
                          
                          { (isProposalEvent || eventTypeLabel === 'counter_proposal') && Number(event.metadata?.amount) > 0 && (
                            <Chip
                              label={`${event.metadata.amount} ${getCurrencySymbol(event.metadata.currency || dispute?.reservation?.currency || 'EUR')}`}
                              color="success"
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          )}

                          {event.actor && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <Avatar 
                                src={getProfilePictureUrl(event.actor)} 
                                sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}
                              >
                                {!getProfilePictureUrl(event.actor) && <PersonIcon fontSize="small" />}
                              </Avatar>
                              <Typography variant="caption" color="text.secondary">
                                {event.actor.name || event.actor.email || t('detail.unknownUser', 'Utilisateur inconnu')}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

                    
        </Grid>

        {/* Colonne droite - Informations complémentaires */}
        <Grid item xs={12} md={4}>
          {/* Parties impliquées */}
          <Card elevation={2} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" />
                {t('detail.partiesTitle')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {dispute.reporter && (
                <Box mb={2}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar 
                      src={getProfilePictureUrl(dispute.reporter)}
                      sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}
                    >
                      {!getProfilePictureUrl(dispute.reporter) && <PersonIcon fontSize="small" />}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">{t('labels.reporter')}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {dispute.reporter.name || dispute.reporter.email || t('detail.unknownUser')}
                      </Typography>
                      {dispute.reservation && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {dispute.reporter.id === dispute.reservation.listing?.owner_id || 
                           dispute.reporter.id === dispute.reservation.listing?.ownerId ||
                           dispute.reporter.id === dispute.reservation.listing?.owner?.id
                            ? `(${t('reservation.owner')})`
                            : dispute.reporter.id === dispute.reservation.renter_id ||
                              dispute.reporter.id === dispute.reservation.renterId ||
                              dispute.reporter.id === dispute.reservation.renter?.id
                            ? `(${t('reservation.renter')})`
                            : ''}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}
              {dispute.reportedUser && (
                <Box>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar 
                      src={getProfilePictureUrl(dispute.reportedUser)}
                      sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}
                    >
                      {!getProfilePictureUrl(dispute.reportedUser) && <PersonIcon fontSize="small" />}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">{t('labels.accused')}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {dispute.reportedUser.name || dispute.reportedUser.email || t('detail.unknownUser')}
                      </Typography>
                      {dispute.reservation && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {dispute.reportedUser.id === dispute.reservation.listing?.owner_id || 
                           dispute.reportedUser.id === dispute.reservation.listing?.ownerId ||
                           dispute.reportedUser.id === dispute.reservation.listing?.owner?.id
                            ? `(${t('reservation.owner')})`
                            : dispute.reportedUser.id === dispute.reservation.renter_id ||
                              dispute.reportedUser.id === dispute.reservation.renterId ||
                              dispute.reportedUser.id === dispute.reservation.renter?.id
                            ? `(${t('reservation.renter')})`
                            : ''}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Réservation liée */}
          {dispute.reservation && (
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon color="primary" />
                  {t('detail.reservationTitle')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {dispute.reservation.listing?.title && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">{t('labels.listing')}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {dispute.reservation.listing.title}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">{t('labels.reference')}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {dispute.reservation.reference || dispute.reservation.id || 'N/A'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          {actionType === 'resolve' && t('actions.resolveTitle', 'Résoudre le litige')}
          {actionType === 'close' && t('actions.closeTitle', 'Fermer le litige')}
          {actionType === 'escalate' && t('actions.escalateTitle', 'Escalader le litige')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph sx={{ mt: 1 }}>
            {actionType === 'resolve' && t('actions.resolveDesc', 'Indiquez la solution retenue et le montant de compensation éventuel.')}
            {actionType === 'close' && t('actions.closeDesc', 'Fermer ce litige sans action supplémentaire.')}
            {actionType === 'escalate' && t('actions.escalateDesc', 'Transférer ce litige à l\'équipe support pour arbitrage.')}
          </Typography>
          
          {actionType === 'resolve' && (
            <TextField
              fullWidth
              label={t('labels.compensationAmount', 'Montant de compensation (EUR)')}
              type="number"
              value={compensationAmount}
              onChange={(e) => setCompensationAmount(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('labels.note', 'Note / Message')}
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            placeholder={t('placeholders.actionNote', 'Expliquez votre décision...')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>{t('common.cancel', 'Annuler')}</Button>
          <Button 
            onClick={handleActionSubmit} 
            variant="contained" 
            color={actionType === 'resolve' ? 'success' : actionType === 'escalate' ? 'warning' : 'primary'}
            disabled={sending}
          >
            {t('common.confirm', 'Confirmer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Proposal Dialog */}
      <Dialog open={proposalDialogOpen} onClose={() => setProposalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('amicable.proposeTitle', 'Proposer une résolution amiable')}</DialogTitle>
        <DialogContent>
            {proposalError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {proposalError}
              </Alert>
            )}
          <Typography variant="body2" paragraph sx={{ mt: 1 }}>
            {t('amicable.proposeDesc', 'Proposez un montant pour clore ce litige. Si l\'autre partie accepte, le paiement sera déclenché automatiquement.')}
          </Typography>

          {/* 🔄 NOUVEAU: Choix du type de compensation */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('amicable.compensationType', 'Type de compensation')}
            </FormLabel>
            <RadioGroup
              value={compensationType}
              onChange={(e) => setCompensationType(e.target.value)}
            >
              {!isRenter && (
                <FormControlLabel 
                  value="owner_compensation" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        🏠 {t('amicable.ownerCompensation', 'Dédommagement au propriétaire')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('amicable.ownerCompensationDesc', 'Le locataire a causé des dommages → le montant sera prélevé de sa caution')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
                />
              )}
              <FormControlLabel 
                value="renter_refund" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      👤 {t('amicable.renterRefund', 'Remboursement au locataire')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('amicable.renterRefundDesc', 'Le service n\'était pas conforme → le locataire sera remboursé de son paiement')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
              />
              <FormControlLabel 
                value="reservation_cancellation" 
                control={<Radio />} 
                disabled={dispute?.reservation?.status !== 'confirmed'}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      🛑 {t('amicable.cancellation', 'Annulation pure et simple')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dispute?.reservation?.status !== 'confirmed'
                        ? t('amicable.cancellationDisabled', 'Non disponible après le pickup : réservation non confirmée ou déjà démarrée')
                        : t('amicable.cancellationDesc', 'Met fin à la réservation sans compensation financière')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
              />
            </RadioGroup>
          </FormControl>

          {/* Affichage conditionnel du montant max selon le type */}
          {compensationType === 'owner_compensation' && dispute?.reservation?.deposit?.amount && (
            <Alert severity="info" sx={{ mb: 2 }}>
              💰 {t('amicable.depositInfo', 'Montant maximum disponible (caution) :')} <strong>{dispute.reservation.deposit.amount} {getCurrencySymbol(dispute.reservation.deposit.currency || dispute.reservation?.currency || 'EUR')}</strong>
            </Alert>
          )}
          
          {compensationType === 'renter_refund' && (() => {
            // Calculer le total des paiements rental (ce que le renter a paye pour la location)
            const rentalPayments = dispute?.reservation?.payments || [];
            const totalRentalPaid = rentalPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
            return (
              <Alert severity="info" sx={{ mb: 2 }}>
                💳 {t('amicable.rentalPaymentInfo', 'Montant maximum remboursable (paiement location) :')} <strong>{totalRentalPaid.toFixed(2)} {getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</strong>
              </Alert>
            );
          })()}

          {compensationType === 'reservation_cancellation' ? (
            (() => {
              const rentalPayments = dispute?.reservation?.payments || [];
              const rentalOnlyPayments = rentalPayments.filter(p => p.payment_type === 'rental');
              const rentalPaymentMethods = Array.from(new Set(rentalOnlyPayments.map(p => (p.payment_method || '').toLowerCase()).filter(Boolean)));
              const allRentalPaidInCash = rentalPaymentMethods.length > 0 && rentalPaymentMethods.every(m => m === 'cash');

              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('amicable.cancellationInfo', 'Aucune somme ne sera échangée. La réservation sera annulée si l’autre partie accepte.')}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                    <Typography variant="body2" fontWeight="bold" color="info.dark" gutterBottom>
                      💰 {t('amicable.cancellationFinancialConsequences', 'Conséquences financières :')}
                    </Typography>
                    {allRentalPaidInCash ? (
                      <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                        {t('amicable.cancellationCashNote', "Paiement en espèces détecté — le locataire n'a pas payé via la plateforme. Tant que l'annulation est possible, cela signifie que le locataire n'a pas encore payé la location ni la caution.")}
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                          • {t('amicable.renterFullRefund', 'Le locataire sera intégralement remboursé de son paiement de location')}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                          • {t('amicable.depositReleased', 'La caution sera libérée (aucune capture)')}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Alert>
              );
            })()
          ) : (
            <TextField
              fullWidth
              label={t('labels.compensationAmount', `Montant propose (${getCurrencySymbol(dispute?.reservation?.currency || 'EUR')})`)}
              type="number"
              value={proposalAmount}
              onChange={(e) => setProposalAmount(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</InputAdornment>,
                inputProps: { 
                  min: 0.01, 
                  step: 0.01,
                  max: compensationType === 'owner_compensation' 
                    ? (dispute?.reservation?.deposit?.amount || undefined) 
                    : ((dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || undefined)
                }
              }}
              error={
                (proposalAmount !== '' && parseFloat(proposalAmount) <= 0) ||
                (compensationType === 'owner_compensation' && proposalAmount && dispute?.reservation?.deposit?.amount && parseFloat(proposalAmount) > dispute.reservation.deposit.amount) ||
                (compensationType === 'renter_refund' && proposalAmount && parseFloat(proposalAmount) > (dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))
              }
              helperText={
                proposalAmount !== '' && parseFloat(proposalAmount) <= 0
                  ? t('amicable.amountMustBePositive', 'Le montant doit être supérieur à 0')
                  : compensationType === 'owner_compensation' && proposalAmount && dispute?.reservation?.deposit?.amount && parseFloat(proposalAmount) > dispute.reservation.deposit.amount 
                    ? t('amicable.amountExceedsDeposit', 'Le montant ne peut pas depasser la caution')
                    : compensationType === 'renter_refund' && proposalAmount && parseFloat(proposalAmount) > (dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
                      ? t('amicable.amountExceedsRental', 'Le montant ne peut pas depasser le paiement de location')
                      : ''
              }
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('labels.message', 'Raison de la proposition (Obligatoire)')}
            value={proposalMessage}
            onChange={(e) => setProposalMessage(e.target.value)}
            placeholder={t('placeholders.proposalMessage', 'Expliquez votre proposition...')}
            error={!proposalMessage.trim()}
            helperText={!proposalMessage.trim() ? t('validation.messageRequired', 'Ce champ est obligatoire') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setProposalDialogOpen(false);
            setCompensationType(defaultCompensationType);
          }}>{t('common.cancel', 'Annuler')}</Button>
          <Button 
            onClick={handleProposeResolution} 
            variant="contained" 
            color="primary"
            disabled={
              sending || 
              !proposalMessage.trim() || 
              (compensationType !== 'reservation_cancellation' && (!proposalAmount || parseFloat(proposalAmount) <= 0)) || 
              (compensationType === 'owner_compensation' && dispute?.reservation?.deposit?.amount && parseFloat(proposalAmount) > dispute.reservation.deposit.amount) ||
              (compensationType === 'renter_refund' && parseFloat(proposalAmount) > (dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))
            }
          >
            {t('common.sendProposal', 'Envoyer la proposition')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Proposal Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>
          ❌ {t('amicable.rejectTitle', 'Refuser la proposition')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph sx={{ mt: 1 }}>
            {dispute?.metadata?.amicable_proposal?.compensation_type === 'reservation_cancellation'
              ? t('amicable.rejectDescCancellation', 'Vous êtes sur le point de refuser la proposition d\'annulation de la réservation.')
              : (<>
                  {t('amicable.rejectDesc', 'Vous êtes sur le point de refuser la proposition de')} <strong>{dispute?.metadata?.amicable_proposal?.amount} {getCurrencySymbol(dispute?.metadata?.amicable_proposal?.currency || dispute?.reservation?.currency || 'EUR')}</strong>.
                </>)}
          </Typography>
          <Typography variant="body2" paragraph color="text.secondary">
            {t('amicable.rejectInfo', 'L\'autre partie sera notifiée et pourra faire une nouvelle proposition.')}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('labels.rejectReason', 'Raison du refus (Obligatoire)')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('placeholders.rejectReason', 'Expliquez pourquoi vous refusez cette proposition...')}
            error={!rejectReason.trim()}
            helperText={
              !rejectReason.trim()
                ? t('validation.messageRequired', 'La raison du refus est obligatoire')
                : ''
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>{t('common.cancel', 'Annuler')}</Button>
          <Button 
            onClick={handleRejectProposal} 
            variant="contained" 
            color="error"
            disabled={sending || !rejectReason.trim()}
          >
            {t('amicable.confirmReject', 'Confirmer le refus')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Counter-Proposal Dialog */}
      <Dialog open={counterProposalDialogOpen} onClose={() => setCounterProposalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'primary.main' }}>
          🔄 {t('amicable.counterTitle', 'Faire une contre-proposition')}
        </DialogTitle>
        <DialogContent>
            {counterError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {counterError}
              </Alert>
            )}
          <Typography variant="body2" paragraph sx={{ mt: 1 }}>
            {t('amicable.counterDesc', 'La proposition actuelle de')} <strong>{dispute?.metadata?.amicable_proposal?.amount} {getCurrencySymbol(dispute?.metadata?.amicable_proposal?.currency || dispute?.reservation?.currency || 'EUR')}</strong> {t('amicable.counterDesc2', 'sera automatiquement refusée et remplacée par votre nouvelle proposition.')}
          </Typography>

          {/* 🔄 Choix du type de compensation (peut être différent de la proposition originale) */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
              {t('amicable.compensationType', 'Type de compensation')}
            </FormLabel>
            <RadioGroup
              value={counterCompensationType}
              onChange={(e) => setCounterCompensationType(e.target.value)}
            >
              {!isRenter && (
                <FormControlLabel 
                  value="owner_compensation" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        🏠 {t('amicable.ownerCompensation', 'Dédommagement au propriétaire')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('amicable.ownerCompensationDesc', 'Le locataire a causé des dommages → le montant sera prélevé de sa caution')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
                />
              )}
              <FormControlLabel 
                value="renter_refund" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      👤 {t('amicable.renterRefund', 'Remboursement au locataire')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('amicable.renterRefundDesc', 'Le service n\'était pas conforme → le locataire sera remboursé de son paiement')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
              />
              <FormControlLabel
                value="reservation_cancellation"
                control={<Radio />}
                disabled={dispute?.reservation?.status !== 'confirmed'}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      🛑 {t('amicable.cancellation', 'Annulation pure et simple')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dispute?.reservation?.status !== 'confirmed'
                        ? t('amicable.cancellationDisabled', 'Non disponible après le pickup : réservation non confirmée ou déjà démarrée')
                        : t('amicable.cancellationDesc', 'Met fin à la réservation sans compensation financière')}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { mt: 0.5 } }}
              />
            </RadioGroup>
          </FormControl>

          {/* Affichage conditionnel du montant max selon le type de contre-proposition */}
          {counterCompensationType === 'owner_compensation' && dispute?.reservation?.deposit?.amount && (
            <Alert severity="info" sx={{ mb: 2 }}>
              💰 {t('amicable.depositInfo', 'Montant maximum disponible (caution) :')} <strong>{dispute.reservation.deposit.amount} {getCurrencySymbol(dispute.reservation.deposit.currency || dispute.reservation?.currency || 'EUR')}</strong>
            </Alert>
          )}
          
          {counterCompensationType === 'renter_refund' && (() => {
            const rentalPayments = dispute?.reservation?.payments || [];
            const totalRentalPaid = rentalPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
            return (
              <Alert severity="info" sx={{ mb: 2 }}>
                  💳 {t('amicable.rentalPaymentInfo', 'Montant maximum remboursable (paiement location) :')} <strong>{totalRentalPaid.toFixed(2)} {getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</strong>
                </Alert>
            );
          })()}
          
          {counterCompensationType === 'reservation_cancellation' ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('amicable.cancellationInfo', 'Aucune somme ne sera échangée. La réservation sera annulée si l’autre partie accepte.')}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                <Typography variant="body2" fontWeight="bold" color="info.dark" gutterBottom>
                  💰 {t('amicable.cancellationFinancialConsequences', 'Conséquences financières :')}
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                  • {t('amicable.renterFullRefund', 'Le locataire sera intégralement remboursé de son paiement de location')}
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                  • {t('amicable.depositReleased', 'La caution sera libérée (aucune capture)')}
                </Typography>
              </Box>
            </Alert>
          ) : (
            <TextField
              fullWidth
              label={t('labels.counterAmount', `Votre montant (${dispute?.reservation?.currency || 'EUR'})`)}
              type="number"
              value={counterAmount}
              onChange={(e) => setCounterAmount(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{getCurrencySymbol(dispute?.reservation?.currency || 'EUR')}</InputAdornment>,
                inputProps: { 
                  min: 0.01, 
                  step: 0.01,
                  max: counterCompensationType === 'owner_compensation' 
                    ? (dispute?.reservation?.deposit?.amount || undefined) 
                    : ((dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) || undefined)
                }
              }}
              error={
                (counterAmount !== '' && parseFloat(counterAmount) <= 0) ||
                (counterCompensationType === 'owner_compensation' && counterAmount && dispute?.reservation?.deposit?.amount && parseFloat(counterAmount) > dispute.reservation.deposit.amount) ||
                (counterCompensationType === 'renter_refund' && counterAmount && parseFloat(counterAmount) > (dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))
              }
              helperText={
                counterAmount !== '' && parseFloat(counterAmount) <= 0
                  ? t('amicable.amountMustBePositive', 'Le montant doit être supérieur à 0')
                  : counterCompensationType === 'owner_compensation' && counterAmount && dispute?.reservation?.deposit?.amount && parseFloat(counterAmount) > dispute.reservation.deposit.amount 
                    ? t('amicable.amountExceedsDeposit', 'Le montant ne peut pas dépasser la caution')
                    : counterCompensationType === 'renter_refund' && counterAmount && parseFloat(counterAmount) > (dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
                      ? t('amicable.amountExceedsRental', 'Le montant ne peut pas dépasser le paiement de location')
                      : ''
              }
            />
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('labels.counterMessage', 'Message (Obligatoire)')}
            value={counterMessage}
            onChange={(e) => setCounterMessage(e.target.value)}
            placeholder={t('placeholders.counterMessage', 'Expliquez votre contre-proposition...')}
            error={!counterMessage.trim()}
            helperText={
              !counterMessage.trim()
                ? t('validation.messageRequired', 'Le message est obligatoire')
                : t('validation.messageAuto', 'Message auto-généré - vous pouvez le modifier')
            }
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCounterProposalDialogOpen(false)}>{t('common.cancel', 'Annuler')}</Button>
          <Button 
            onClick={handleCounterProposal} 
            variant="contained" 
            color="primary"
            disabled={
              sending || 
              !counterMessage.trim() ||
              (counterCompensationType !== 'reservation_cancellation' && (!counterAmount || parseFloat(counterAmount) <= 0)) || 
              (counterCompensationType === 'owner_compensation' && dispute?.reservation?.deposit?.amount && parseFloat(counterAmount) > dispute.reservation.deposit.amount) ||
              (counterCompensationType === 'renter_refund' && parseFloat(counterAmount) > (dispute?.reservation?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))
            }
          >
            {t('amicable.sendCounter', 'Envoyer la contre-proposition')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Accept Confirmation Dialog */}
      <Dialog open={acceptConfirmDialogOpen} onClose={() => { setAcceptConfirmDialogOpen(false); setAcceptError(''); }} maxWidth="sm" fullWidth>
        <LoadingOverlay
          isOpen={sending}
          title={t('amicable.acceptProcessingTitle', 'Acceptation en cours...')}
          subtitle={t('amicable.acceptProcessingSubtitle', 'Nous traitons l\'acceptation et le paiement. Veuillez patienter...')}
          warning={t('amicable.doNotClose', 'Ne fermez pas cette page.')}
          primaryColor="#6200ee"
          accentColor="#03dac4"
        />
        <DialogTitle sx={{ color: 'success.main' }}>
          ✅ {t('amicable.confirmAcceptTitle', 'Confirmer l\'acceptation')}
        </DialogTitle>
        <DialogContent>
          {sending && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress color="success" />
            </Box>
          )}
          {acceptError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAcceptError('')}>
              {acceptError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            <AlertTitle>{t('amicable.confirmAcceptWarning', 'Attention')}</AlertTitle>
            {t('amicable.confirmAcceptDesc', 'Vous êtes sur le point d\'accepter la proposition suivante :')}
          </Alert>
          
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              {dispute?.metadata?.amicable_proposal?.compensation_type === 'owner_compensation' 
                ? t('amicable.ownerCompensation', 'Dédommagement au propriétaire')
                : dispute?.metadata?.amicable_proposal?.compensation_type === 'renter_refund'
                  ? t('amicable.renterRefund', 'Remboursement au locataire')
                  : t('amicable.cancellation', 'Annulation de la réservation')
              }
            </Typography>
            {dispute?.metadata?.amicable_proposal?.compensation_type !== 'reservation_cancellation' && (
              <Typography variant="h5" color="primary" sx={{ mt: 1 }}>
                {dispute?.metadata?.amicable_proposal?.amount} {getCurrencySymbol(dispute?.metadata?.amicable_proposal?.currency || 'EUR')}
              </Typography>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary">
              {dispute?.metadata?.amicable_proposal?.compensation_type === 'reservation_cancellation'
              ? (() => {
                const rentalPayments = dispute?.reservation?.payments || [];
                const rentalOnlyPayments = rentalPayments.filter(p => p.payment_type === 'rental');
                const rentalPaymentMethods = Array.from(new Set(rentalOnlyPayments.map(p => (p.payment_method || '').toLowerCase()).filter(Boolean)));
                const allRentalPaidInCash = rentalPaymentMethods.length > 0 && rentalPaymentMethods.every(m => m === 'cash');

                return (
                  <>
                    {t('amicable.confirmAcceptNoteCancellation', 'Cette action est irréversible. La réservation sera annulée et le litige clos après votre confirmation.')}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="body2" fontWeight="bold" color="info.dark" gutterBottom>
                        💰 {t('amicable.cancellationFinancialConsequences', 'Conséquences financières :')}
                      </Typography>
                      {allRentalPaidInCash ? (
                        <>
                          <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                            {t('amicable.cancellationCashNote', "Paiement en espèces détecté — le locataire n'a pas payé via la plateforme. Tant que l'annulation est possible, cela signifie que le locataire n'a pas encore payé la location ni la caution.")}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                            • {t('amicable.renterFullRefund', 'Le locataire sera intégralement remboursé de son paiement de location')}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ ml: 2 }}>
                            • {t('amicable.depositReleased', 'La caution sera libérée (aucune capture)')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </>
                );
              })()
              : (() => {
                const rentalPayments = dispute?.reservation?.payments || [];
                const rentalOnlyPayments = rentalPayments.filter(p => p.payment_type === 'rental');
                const rentalPaymentMethods = Array.from(new Set(rentalOnlyPayments.map(p => (p.payment_method || '').toLowerCase()).filter(Boolean)));
                const allRentalPaidInCash = rentalPaymentMethods.length > 0 && rentalPaymentMethods.every(m => m === 'cash');

                return allRentalPaidInCash
                  ? t('amicable.confirmAcceptNoteCash', "Paiement en espèces détecté — aucun règlement en ligne ne sera initié. Merci de coordonner le remboursement hors-plateforme.")
                  : t('amicable.confirmAcceptNote', 'Cette action est irréversible. Le règlement sera initié après votre confirmation.');
              })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAcceptConfirmDialogOpen(false); setAcceptError(''); }}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleAcceptProposal} 
            variant="contained" 
            color="success"
            disabled={sending}
            startIcon={<CheckCircleIcon />}
          >
            {t('amicable.confirmAccept', 'Oui, j\'accepte')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Escalate to Admin Dialog */}
      <Dialog open={escalateDialogOpen} onClose={() => { setEscalateDialogOpen(false); setEscalateError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'warning.main' }}>
          ⚠️ {t('escalate.title', 'Escalader à un administrateur')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
            <AlertTitle>{t('escalate.warning', 'Action importante')}</AlertTitle>
            {t('escalate.warningDesc', 'En escaladant ce litige, un administrateur prendra en charge la résolution. Les négociations amiables seront suspendues.')}
          </Alert>

          {escalateError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEscalateError(null)}>
              {escalateError}
            </Alert>
          )}
          
          <Typography variant="body2" paragraph>
            {t('escalate.desc', 'Si vous ne parvenez pas à trouver un accord avec l\'autre partie, vous pouvez demander l\'intervention d\'un administrateur qui tranchera de manière impartiale.')}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('labels.escalateReason', 'Raison de l\'escalade')}
            value={escalateReason}
            onChange={(e) => setEscalateReason(e.target.value)}
            placeholder={t('placeholders.escalateReason', 'Expliquez pourquoi vous souhaitez escalader ce litige...')}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEscalateDialogOpen(false); setEscalateError(null); }}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button 
            onClick={handleEscalateToAdmin} 
            variant="contained" 
            color="warning"
            disabled={sending || !escalateReason.trim()}
            startIcon={<WarningIcon />}
          >
            {t('escalate.confirm', 'Escalader le litige')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3DS Authentication Modal */}
      <DisputeAuthenticationModal
        open={authModalOpen}
        onClose={() => {
          if (!authProcessing) {
            setAuthModalOpen(false);
            setAuthClientSecret(null);
            setAuthPaymentMethodId(null);
            setAuthPaymentIntentId(null);
          }
        }}
        clientSecret={authClientSecret}
        paymentMethodId={authPaymentMethodId}
        paymentIntentId={authPaymentIntentId}
        disputeId={disputeId}
        onSuccess={handleAuthenticationSuccess}
        onError={handleAuthenticationError}
        onCancel={handleAuthenticationCancel}
      />

      {/* Modal 3DS pour paiement admin */}
      <AdminPaymentAuth3DSModal
        open={adminAuthModalOpen}
        onClose={() => setAdminAuthModalOpen(false)}
        authRequest={adminAuthRequest}
        currency={dispute?.reservation?.currency || 'EUR'}
        onSuccess={() => {
          setAdminAuthModalOpen(false);
          fetchDispute();
          pollDisputeUntilFinal();
        }}
      />

      {/* 🆕 Dialog Débannissement */}
      <Dialog open={unbanDialogOpen} onClose={() => !unbanSubmitting && setUnbanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('ban.unbanTitle', 'Débannir un utilisateur')}</DialogTitle>
        <DialogContent>
          {unbanUserInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('labels.user', 'Utilisateur :')} {unbanForm.userName}
              </Typography>
              <Typography variant="body2">
                <strong>{t('labels.name', 'Nom :')}</strong> {unbanUserInfo.name}
              </Typography>
              <Typography variant="body2">
                <strong>{t('labels.email', 'Email :')}</strong> {unbanUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>{t('labels.language', 'Langue :')}</strong> {unbanUserInfo.language || 'en'}
              </Typography>
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('ban.unbanWarning', 'Cette action débannira l\'utilisateur <strong>{{userId}}</strong> et réactivera toutes ses annonces.', { userId: unbanForm.userId })}
            <br />
            {t('ban.unbanNotify', 'Une notification sera envoyée dans la langue de préférence de l\'utilisateur.')}
          </Alert>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('ban.unbanReasonLabel', 'Raison du débannissement')}
            value={unbanForm.reason}
            onChange={(e) => setUnbanForm(prev => ({ ...prev, reason: e.target.value }))}
            placeholder={t('ban.unbanReasonPlaceholder', "Expliquez la raison du débannissement (sera incluse dans l'email de notification)")}
            required
            helperText={t('ban.unbanReasonHelper', "Cette raison sera envoyée à l'utilisateur par email")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnbanDialogOpen(false)} disabled={unbanSubmitting}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleUnbanUser}
            disabled={unbanSubmitting || !unbanForm.reason?.trim()}
          >
            {unbanSubmitting ? <CircularProgress size={20} /> : t('ban.unban', 'Débannir')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DisputeDetailPage;